/**
 * COP Dashboard Map Component using Mapbox GL
 * Shows live device locations with geofencing support
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Map, { Source, Layer, Marker, Popup, ScaleControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, useTheme, Paper, Divider, Button, Chip } from '@mui/material';
import { 
  MyLocation as CenterIcon,
  Power as PowerIcon,
  Bluetooth as BluetoothIcon,
  Wifi as WifiIcon,
  SignalCellularAlt as CellularIcon,
  FlightTakeoff as AirplaneIcon,
  Circle as CircleIcon,
  ReportProblem as SOSIcon,
  Bookmark as BookmarkIcon,
  Place as PlaceIcon,
  Groups,
  LocationOn,
  NotificationsActive,
  PushPin,
  Star,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Satellite as SatelliteIcon,
  Map as MapIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  Streetview as StreetViewIcon,
  Videocam as CameraGlyphIcon,
  Layers as MapLayersIcon,
} from '@mui/icons-material';
import { DeviceLocationStatus, DroneCoverageResponse, copApiService } from '../services/copApi';
import {
  COTRAVELER_PULSE_COLOR,
  COTRAVELER_TINT_RING,
  devicesFollowedByCotraveler,
  liveCotravelerAlerts,
} from '../utils/cotravelerFollow';
import {
  MissionEvent,
  MissionRoute,
  missionEventLabel,
  missionSeverityColor,
} from '../services/missionEventsService';
import GeofenceDrawControl from './GeofenceDrawControl';
import { MapStyleType } from './MapStyleControl';
import type { TrafficIncident, WeatherAlert } from '@signals/app-shell/services/areaIntelService';
import MapTrafficIncidents from '@signals/app-shell/features/map/MapTrafficIncidents';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCameraLocationsOverlay, type CameraFacetItem } from '@signals/app-shell/hooks/useCameraLocationsOverlay';
import {
  CAMERA_OVERLAY_LAYER_IDS,
  parseCameraFeatureProperties,
  type CameraLocationProperties,
} from '@signals/app-shell/services/cameraOverlayService';
import CameraLocationsMapLayers from '@signals/app-shell/components/CameraLocationsMapLayers';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupShell,
  DarkMapPopupRow,
  DarkMapPopupCoords,
  darkMapPopupActionBtnStyle,
  useDarkMapPopupChrome,
} from '@signals/app-shell/components/map/DarkMapPopup';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import CameraLocationPopupContent from '@signals/app-shell/components/CameraLocationPopupContent';
import { useReferenceLayers } from '@signals/app-shell/hooks/useReferenceLayers';
import ReferenceLayersMapLayers, {
  referenceLayerIdFromMapLayer,
  referenceLayerInteractiveIds,
} from '@signals/app-shell/components/map/ReferenceLayersMapLayers';
import ReferenceFeaturePopupContent from '@signals/app-shell/components/map/ReferenceFeaturePopupContent';
import MapMeasureLayers from '@signals/app-shell/components/map/MapMeasureLayers';
import MapMeasureReadout from '@signals/app-shell/components/map/MapMeasureReadout';
import { nextMeasurePoints, type MeasureMode } from '@signals/app-shell/services/mapMeasure';
import { useAuthStore } from '@signals/app-shell/stores';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import {
  IntelPopupCard,
  INTEL_COLORS,
  INTEL_POPUP_CLASS,
} from '@signals/app-shell/components/map/IntelPopup';
import CamerasInViewStrip, {
  type CameraInView,
  type CameraNearbyEntity,
} from '@signals/app-shell/components/map/CamerasInViewStrip';
import {
  cameraHeading,
  streetViewPreviewsOn,
  useCameraStreetViewSettings,
} from '@signals/app-shell/services/cameraStreetViewService';
import {
  COP_NEAR_ENTITIES_KEY,
  DEFAULT_COP_NEAR_ENTITIES,
  annotateCamerasNearPoints,
  loadCameraNearTracksPrefs,
  saveCameraNearTracksPrefs,
  type CameraNearTracksPrefs,
  type TaggedPoint,
} from '@signals/app-shell/services/cameraProximity';
import COPMapLayersDialog from './COPMapLayersDialog';
import { useCopMapLayerPrefs } from '../utils/copMapLayerPrefs';
import { COP_DEVICE_COLORS, classifyDeviceColor } from '../utils/deviceColors';
import { normalizeDeviceType } from '../utils/normalizeDeviceType';
import { computeCameraFootprint, resolveCameraYawDeg } from '../utils/cameraFootprint';
import { formatSpeedMph } from '../utils/speed';
import LiveStreamPlayer from './LiveStreamPlayer';
import type { ItinerarySubmission } from '@signals/app-shell/services/itineraryApi';
import {
  formatAltitudeAgl,
  formatArea,
  formatBearing,
  formatDistanceFromHome,
  formatFov,
  formatGimbalMode,
  formatGimbalPitch,
  formatVerticalSpeed,
  satelliteQuality,
} from '../utils/droneTelemetry';
import DroneTelemetryOverlay from './DroneTelemetryOverlay';

/**
 * A popup row that disappears when there is nothing to say.
 *
 * The aircraft rows are numerous and each is independently optional — a gimbal
 * that has not reported, a home point not yet set — and printing "N/A" for a
 * dozen of them buries the fields that do have values.
 */
const OptionalPopupRow: React.FC<{ label: string; value: string | null | undefined }> = ({
  label,
  value,
}) => (value ? <DarkMapPopupRow label={label}>{value}</DarkMapPopupRow> : null);

// ===========================================
// MARKER CONSTANTS AND HELPERS FOR SYMBOL LAYER
// GPU-accelerated markers for smooth map dragging
// ===========================================

// Marker fill colors come from the shared COP color palette (see
// utils/deviceColors). Keeping the alias here just preserves the
// `MARKER_COLORS` symbol that the sprite loader iterates over.
const MARKER_COLORS = COP_DEVICE_COLORS;

/** Invisible layer that reference layers insert beneath (keeps them under app layers). */
const REF_LAYERS_ANCHOR_ID = 'ref-layers-anchor';

// Generate a pin SVG for a given color (label rendered separately via text-field)
const createPinImageSVG = (color: string): string => {
  return `<svg width="32" height="40" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="38" rx="6" ry="2" fill="rgba(0,0,0,0.3)"/>
    <path d="M16 2 C10 2 6 6 6 12 C6 18 16 35 16 35 C16 35 26 18 26 12 C26 6 22 2 16 2 Z" 
          fill="${color}" stroke="white" stroke-width="2"/>
  </svg>`;
};

// Load marker images into the map's sprite
const loadMarkerImages = (map: mapboxgl.Map): void => {
  Object.entries(MARKER_COLORS).forEach(([key, color]) => {
    const imageId = `marker-${key}`;
    
    // Skip if already loaded (check must be inside the callback too for async safety)
    if (map.hasImage(imageId)) return;
    
    const img = new Image(32, 40);
    img.onload = () => {
      // Double-check in callback since image loading is async
      if (!map.hasImage(imageId)) {
        try {
          map.addImage(imageId, img);
        } catch (e) {
          // Image might have been added between check and add
          console.debug(`Marker image ${imageId} already exists or failed to add`);
        }
      }
    };
    img.src = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(createPinImageSVG(color))}`;
  });
};

// Marker classification delegates to the shared color util so every
// surface (map, list, legend, stats) classifies the same device the same
// way.
const getMarkerType = (device: DeviceLocationStatus): keyof typeof MARKER_COLORS =>
  classifyDeviceColor(device);

// Helper to get label for marker. Device type is now encoded by pin color
// (phone=green, watch=blue, vehicle=purple — matching the map legend), so
// the label only needs the entity number; the letter prefix would be
// redundant. Falls back to a one-letter label (P/W/V/S/RP/SR) only when
// the entity has no number.
const getMarkerLabelForSymbol = (device: DeviceLocationStatus): string => {
  const entityId = device.entity_id || '';
  const entityNumber = entityId.match(/[-\s](\d+)$/)?.[1] || '';
  const markerType = getMarkerType(device);

  if (markerType === 'rp') return entityNumber || 'RP';
  if (markerType === 'srp') return entityNumber || 'SR';
  if (markerType === 'phone') return entityNumber ? entityNumber.padStart(2, '0') : 'P';
  if (markerType === 'watch') return entityNumber ? entityNumber.padStart(2, '0') : 'W';
  if (markerType === 'vehicle') return entityNumber ? entityNumber.padStart(2, '0') : 'V';
  if (markerType === 'drone') return entityNumber ? entityNumber.padStart(2, '0') : 'D';
  if (markerType === 'wardriver') return entityNumber || 'B';
  if (markerType === 'student') return entityNumber || 'S';
  return '?';
};

// Helper to get opacity based on last seen
const getMarkerOpacity = (device: DeviceLocationStatus): number => {
  if (!device.last_seen_minutes_ago) return 1;
  if (device.last_seen_minutes_ago <= 30) return 1;
  if (device.last_seen_minutes_ago <= 1440) return 0.7;
  return 0.4;
};

// Convert devices array to GeoJSON for Symbol Layer rendering
const devicesToGeoJSON = (devices: DeviceLocationStatus[], selectedDeviceId?: string): GeoJSON.FeatureCollection => {
  return {
    type: 'FeatureCollection',
    features: devices.map((device) => ({
      type: 'Feature',
      properties: {
        id: device.device_id,
        markerType: getMarkerType(device),
        label: getMarkerLabelForSymbol(device),
        opacity: getMarkerOpacity(device),
        isSelected: device.device_id === selectedDeviceId,
        // Store full device data for click handling (as JSON string)
        deviceData: JSON.stringify(device),
      },
      geometry: {
        type: 'Point',
        coordinates: [device.longitude, device.latitude],
      },
    })),
  };
};

/** Default phone back-camera FOV / cone length when device does not send overrides. */
const VIEW_CONE_FOV_DEG = 65;
const VIEW_CONE_RANGE_M = 75;

/** How far back the area-observed layer looks for a selected aircraft. */
const AREA_OBSERVED_WINDOW_MS = 2 * 60 * 60 * 1000;

/** Destination point (haversine) — bearing degrees clockwise from north. */
function destinationPoint(
  lat: number,
  lon: number,
  distanceM: number,
  bearingDeg: number
): [number, number] {
  const R = 6378137;
  const δ = distanceM / R;
  const θ = (bearingDeg * Math.PI) / 180;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lon * Math.PI) / 180;
  const sinφ1 = Math.sin(φ1);
  const cosφ1 = Math.cos(φ1);
  const sinδ = Math.sin(δ);
  const cosδ = Math.cos(δ);
  const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const λ2 =
    λ1 + Math.atan2(Math.sin(θ) * sinδ * cosφ1, cosδ - sinφ1 * sinφ2);
  return [(λ2 * 180) / Math.PI, (φ2 * 180) / Math.PI];
}

/**
 * Ground area each device's camera can see.
 *
 * Two very different things share this layer:
 *
 * - An aircraft gets a real projected footprint, computed from altitude, gimbal
 *   attitude and lens FOV. It is the actual quadrilateral on the ground.
 * - A phone gets the old indicative wedge: a fixed 65 degree / 75 m guess from
 *   heading alone, because a handheld reports no pitch or altitude to do better.
 *
 * The `precise` property tells them apart so the two are not styled as if they
 * carry the same authority.
 */
const cameraCoverageToGeoJSON = (
  devices: DeviceLocationStatus[]
): GeoJSON.FeatureCollection => {
  const features: GeoJSON.Feature[] = [];
  for (const d of devices) {
    if (!Number.isFinite(d.latitude) || !Number.isFinite(d.longitude)) continue;

    if (normalizeDeviceType(d.device_type) === 'Drone') {
      const cameraYawDeg = resolveCameraYawDeg({
        gimbalYawDeg: d.gimbal_yaw_deg,
        gimbalYawRelDeg: d.gimbal_yaw_rel_deg,
        gimbalMode: d.gimbal_mode,
        headingDeg: d.bearing,
      });
      if (cameraYawDeg == null) continue;

      const footprint = computeCameraFootprint({
        latitude: d.latitude,
        longitude: d.longitude,
        altitudeAglM: d.altitude_agl_m,
        gimbalPitchDeg: d.gimbal_pitch_deg,
        cameraYawDeg,
        gimbalRollDeg: d.gimbal_roll_deg ?? 0,
        hfovDeg: d.hfov_deg,
        vfovDeg: d.vfov_deg,
        // A parked aircraft with a level camera projects to the horizon clamp in
        // every direction, which is thousands of acres of fiction.
        isFlying: d.is_flying,
      });
      // Null means the aircraft is on the ground or the telemetry is
      // incomplete — there is genuinely nothing to draw.
      if (!footprint) continue;

      features.push({
        type: 'Feature',
        properties: {
          id: d.device_id,
          label: d.entity_id || d.device_id,
          precise: true,
          horizonClipped: footprint.horizonClipped,
          areaM2: Math.round(footprint.areaM2),
          centerDistanceM: Math.round(footprint.centerDistanceM),
        },
        geometry: { type: 'Polygon', coordinates: [footprint.ring] },
      });
      continue;
    }

    // Phones: indicative wedge, and only while actually streaming.
    if (!d.is_streaming) continue;
    const heading = d.bearing;
    if (heading == null || !Number.isFinite(heading)) continue;

    const fov = VIEW_CONE_FOV_DEG;
    const rangeM = VIEW_CONE_RANGE_M;
    const start = heading - fov / 2;
    const end = heading + fov / 2;
    const steps = 16;
    const ring: [number, number][] = [[d.longitude, d.latitude]];
    for (let i = 0; i <= steps; i++) {
      const b = start + ((end - start) * i) / steps;
      ring.push(destinationPoint(d.latitude, d.longitude, rangeM, b));
    }
    ring.push([d.longitude, d.latitude]);
    features.push({
      type: 'Feature',
      properties: {
        id: d.device_id,
        heading,
        label: d.entity_id || d.device_id,
        precise: false,
        horizonClipped: false,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [ring],
      },
    });
  }
  return { type: 'FeatureCollection', features };
};

interface SelectedDwell {
  latitude: number;
  longitude: number;
  entity_id: string;
  duration: string;
  start_time: string;
}

// Format duration string like "0:06:28.362000" to "6h 28m"
const formatDwellDuration = (duration: string): string => {
  if (!duration) return 'N/A';
  
  // Handle format like "0:06:28.362000" (days:hours:minutes.microseconds) or "6:28:30"
  const parts = duration.split(':');
  if (parts.length >= 3) {
    const hours = parseInt(parts[0], 10) * 24 + parseInt(parts[1], 10); // days*24 + hours
    const minutes = parseInt(parts[2].split('.')[0], 10);
    
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return '< 1m';
  }
  
  // Fallback - return as is but truncate microseconds
  return duration.split('.')[0];
};

interface AIMapMarker {
  id: string;
  latitude: number;
  longitude: number;
  label: string;
  type: 'colocation' | 'dwell' | 'poi' | 'alert' | 'custom';
  color?: string;
  description?: string;
  entities?: string[];
}

interface AnalysisAreaGeometry {
  type: 'Polygon';
  coordinates: number[][][];
}

interface TrackPoint {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

interface EntityRoute {
  entityId: string;
  tracks: TrackPoint[];
  color?: string;
}

interface DwellCircle {
  id: string;
  latitude: number;
  longitude: number;
  radiusMeters: number;
  entityId: string;
  deviceId?: string;
  durationSeconds: number;
  visitCount?: number;
  startTime?: string;
  endTime?: string;
}

interface DFPMapAlert {
  id: number;
  latitude?: number | null;
  longitude?: number | null;
  severity?: string | null;
  confidence_score?: number | null;
  status?: string | null;
  feedback_verdict?: string | null;
  anomaly_type?: string | null;
  device_id?: string | null;
  evidence?: Record<string, any> | null;
}

type CopMapPopupInfo =
  | { kind: 'device'; device: DeviceLocationStatus }
  | {
      kind: 'dfp';
      alert: DFPMapAlert;
      count: number;
      longitude: number;
      latitude: number;
    }
  | {
      kind: 'watchlist';
      alert: DFPMapAlert;
      longitude: number;
      latitude: number;
    }
  | {
      kind: 'cotraveler';
      alert: DFPMapAlert;
      longitude: number;
      latitude: number;
    }
  | {
      kind: 'itinerary';
      longitude: number;
      latitude: number;
      student_number: string;
      location_name: string;
      route_type: string;
      route_component: string;
      arrive_datetime: string;
      depart_datetime: string;
    }
  | {
      kind: 'camera';
      longitude: number;
      latitude: number;
      camera: CameraLocationProperties;
    }
  | {
      kind: 'reference';
      longitude: number;
      latitude: number;
      layerId: number;
      properties: Record<string, unknown>;
    };

const EMPTY_FEATURE_COLLECTION = {
  type: 'FeatureCollection' as const,
  features: [] as GeoJSON.Feature[],
};

const finiteCoord = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** Observation fix, or the watchlist row's last known place when the hit has none. */
const watchlistPosition = (alert: DFPMapAlert): { latitude: number; longitude: number } | null => {
  const ownLat = finiteCoord(alert.latitude);
  const ownLon = finiteCoord(alert.longitude);
  if (ownLat != null && ownLon != null && !(ownLat === 0 && ownLon === 0)) {
    return { latitude: ownLat, longitude: ownLon };
  }
  const evidence = alert.evidence || {};
  const savedLat = finiteCoord(evidence.watchlist_latitude);
  const savedLon = finiteCoord(evidence.watchlist_longitude);
  if (savedLat != null && savedLon != null && !(savedLat === 0 && savedLon === 0)) {
    return { latitude: savedLat, longitude: savedLon };
  }
  return null;
};

const formatWatchWindow = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const dfpSeverityColor = (severity?: string | null): string => {
  switch ((severity || '').toLowerCase()) {
    case 'critical':
      return '#f44336';
    case 'warning':
      return '#ff9800';
    case 'info':
      return '#2196f3';
    default:
      return '#9e9e9e';
  }
};

interface COPMapGLProps {
  devices: DeviceLocationStatus[];
  selectedDevice: DeviceLocationStatus | null;
  onDeviceSelect: (device: DeviceLocationStatus | null) => void;
  /** Start (device) / stop (null) following a device — used by the popup's Follow action. */
  onFollowDevice?: (device: DeviceLocationStatus | null) => void;
  geofences?: any[];
  onGeofenceDrawn?: (geojson: any) => void;
  isDrawingMode?: boolean;
  selectedGeofenceId?: number | null;
  followedDevice?: DeviceLocationStatus | null;
  onMapBoundsChange?: (devicesInBounds: DeviceLocationStatus[]) => void;
  onGeoBoundsChange?: (bounds: { north: number; south: number; east: number; west: number } | null) => void;
  statusEntries?: any[];
  sosMessages?: any[];
  mapViewState?: { longitude: number; latitude: number; zoom: number } | null;
  onUserInteraction?: () => void;
  selectedDwell?: SelectedDwell | null;
  sidebarOpen?: boolean; // Triggers map resize when sidebar toggles
  isMobile?: boolean; // Hide search bar and shrink controls on mobile
  mobilePanelSize?: 'minimized' | 'half' | 'expanded'; // Triggers map resize on mobile panel changes
  aiMarkers?: AIMapMarker[]; // AI-generated markers from Smart Analytics
  // Area analysis mode
  isDrawingAnalysisArea?: boolean;
  onAnalysisAreaDrawn?: (geometry: AnalysisAreaGeometry) => void;
  onAnalysisAreaCleared?: () => void;
  analysisAreaGeometry?: AnalysisAreaGeometry | null;
  // Entity routes/tracks
  entityRoutes?: EntityRoute[];
  // Dwell circles for displaying stop locations
  dwellCircles?: DwellCircle[];
  // DFP signal alerts for optional heatmap overlay
  dfpAlerts?: DFPMapAlert[];
  // Currently selected DFP alert, highlighted with a strobing ring marker
  selectedDfpAlert?: DFPMapAlert | null;
  // Emitter localization overlay for the expanded DFP alert: estimate pin +
  // accuracy ring + movement track, distinct from the observer-GPS alert pin.
  dfpEmitter?: {
    alertId: number;
    identifier: string;
    label?: string | null;
    observerLat?: number | null;
    observerLon?: number | null;
    estimate: { latitude: number; longitude: number; method: string; observerCount: number; accuracyM: number | null } | null;
    track: Array<{ t: string; latitude: number; longitude: number; observers: number; single_observer: boolean }>;
    registeredTower?: { latitude: number; longitude: number; range_m: number | null } | null;
  } | null;
  trafficIncidents?: TrafficIncident[];
  weatherAlerts?: WeatherAlert[];
  areaAlertsLoading?: boolean;
  onLoadAreaAlerts?: (payload: {
    bounds: { north: number; south: number; east: number; west: number };
    zoom: number;
    force?: boolean;
  }) => void;
  onAreaAlertsViewportChange?: (payload: {
    bounds: { north: number; south: number; east: number; west: number };
    zoom: number;
  }) => void;
  // Student itinerary points (shown when a single entity is selected in COP filters)
  itinerarySubmissions?: ItinerarySubmission[];
  showItinerary?: boolean;
  onShowItineraryChange?: (show: boolean) => void;
  /** True when an itinerary subject entity is pinned — enables the map toolbar button. */
  itineraryToggleEnabled?: boolean;
  /** Past lookback for itinerary points (minutes). Default 120 (2 h). */
  itineraryLookbackMinutes?: number;
  /** Future lookahead for itinerary points (hours). Default 6. */
  itineraryLookaheadHours?: number;
  /** Persist per-user itinerary window preferences. */
  onItineraryWindowChange?: (lookbackMinutes: number, lookaheadHours: number) => void;
  /** Controlled map LIVE pip (Streams tab / external callers). */
  watchLiveDeviceId?: string | null;
  onWatchLiveDeviceIdChange?: (deviceId: string | null) => void;
  /** Planned routes for missions currently under way. */
  missionRoutes?: MissionRoute[];
  /** Threats correlated to those routes; convoy-behaviour events are filtered out upstream. */
  missionThreats?: MissionEvent[];
  onMissionThreatSelect?: (event: MissionEvent) => void;
  /** Camera overlay state for the dashboard Legend tab (null when cameras are off). */
  onCameraLegendChange?: (legend: CopCameraLegend | null) => void;
}

export type CopCameraLegend = {
  /** Cameras currently drawn (after category/source/proximity filters). */
  shownCount: number;
  /** Per-category breakdown with marker colours; empty while loading. */
  categories: CameraFacetItem[];
  loading: boolean;
  error: string | null;
};

const COPMapGL = ({
  devices,
  selectedDevice,
  onDeviceSelect,
  onFollowDevice,
  geofences = [],
  onGeofenceDrawn,
  isDrawingMode = false,
  selectedGeofenceId = null,
  followedDevice = null,
  onMapBoundsChange,
  onGeoBoundsChange,
  statusEntries = [],
  sosMessages = [],
  mapViewState = null,
  onUserInteraction,
  selectedDwell = null,
  sidebarOpen = true,
  isMobile = false,
  mobilePanelSize = 'half',
  aiMarkers = [],
  isDrawingAnalysisArea = false,
  onAnalysisAreaDrawn,
  onAnalysisAreaCleared,
  analysisAreaGeometry = null,
  entityRoutes = [],
  dwellCircles = [],
  dfpAlerts = [],
  selectedDfpAlert = null,
  dfpEmitter = null,
  trafficIncidents = [],
  weatherAlerts = [],
  areaAlertsLoading = false,
  onLoadAreaAlerts,
  onAreaAlertsViewportChange,
  itinerarySubmissions = [],
  showItinerary = false,
  onShowItineraryChange,
  itineraryToggleEnabled = false,
  itineraryLookbackMinutes = 120,
  itineraryLookaheadHours = 6,
  onItineraryWindowChange,
  watchLiveDeviceId: watchLiveDeviceIdProp,
  onWatchLiveDeviceIdChange,
  missionRoutes = [],
  missionThreats = [],
  onMissionThreatSelect,
  onCameraLegendChange,
}: COPMapGLProps) => {
  const { courseSingular } = useCourseLabels();
  const { singular } = useParticipantLabels();
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  const theme = useTheme();
  const [viewport, setViewport] = useState({
    longitude: -110.9265, // Tucson, AZ
    latitude: 32.2217,
    zoom: 11
  });

  const [popupInfo, setPopupInfo] = useState<CopMapPopupInfo | null>(null);
  useDarkMapPopupChrome(!!popupInfo);
  /** Device whose live A/V pip is attached to the moving map pin */
  const [watchingLiveDeviceIdInternal, setWatchingLiveDeviceIdInternal] = useState<string | null>(null);
  const watchingLiveDeviceId =
    watchLiveDeviceIdProp !== undefined ? watchLiveDeviceIdProp : watchingLiveDeviceIdInternal;
  const setWatchingLiveDeviceId = (id: string | null) => {
    if (onWatchLiveDeviceIdChange) onWatchLiveDeviceIdChange(id);
    else setWatchingLiveDeviceIdInternal(id);
  };

  // Keep the pip on the live SSE position (not a stale popup snapshot).
  const livePipDevice = useMemo(() => {
    if (!watchingLiveDeviceId) return null;
    return devices.find((d) => d.device_id === watchingLiveDeviceId) || null;
  }, [watchingLiveDeviceId, devices]);

  // Remember last good lat/lon so brief SSE gaps don't unmount the LiveKit player.
  const lastPipPosRef = useRef<{ deviceId: string; latitude: number; longitude: number } | null>(null);
  if (
    livePipDevice &&
    Number.isFinite(livePipDevice.latitude) &&
    Number.isFinite(livePipDevice.longitude)
  ) {
    lastPipPosRef.current = {
      deviceId: livePipDevice.device_id,
      latitude: livePipDevice.latitude,
      longitude: livePipDevice.longitude,
    };
  }
  const pipAnchor =
    livePipDevice &&
    Number.isFinite(livePipDevice.latitude) &&
    Number.isFinite(livePipDevice.longitude)
      ? { latitude: livePipDevice.latitude, longitude: livePipDevice.longitude }
      : watchingLiveDeviceId &&
          lastPipPosRef.current?.deviceId === watchingLiveDeviceId
        ? {
            latitude: lastPipPosRef.current.latitude,
            longitude: lastPipPosRef.current.longitude,
          }
        : null;

  // Drop the viewer only after presence stays false for a grace period —
  // transient TTL/SSE gaps were tearing down LiveKit and looking like "drops".
  useEffect(() => {
    if (!watchingLiveDeviceId) return;
    if (!livePipDevice) return; // keep watching; position may be stale but player stays up
    if (livePipDevice.is_streaming !== false) return;
    const t = window.setTimeout(() => {
      setWatchingLiveDeviceId(null);
    }, 45_000);
    return () => window.clearTimeout(t);
  }, [watchingLiveDeviceId, livePipDevice]);

  // Admin-uploaded reference layers (boundaries, districts, POIs…): GeoJSON is
  // fetched lazily when a layer is switched on in Map layers; no proximity filter.
  const referenceLayers = useReferenceLayers(true);
  const isAdminUser = useAuthStore((s) => Boolean(s.user?.roles?.includes('administrator')));
  // DFP heatmap only makes sense when the RF Signal module is switched on.
  const dfpModuleEnabled = useFeatureFlagsStore((s) => s.rfDashboard);
  const referenceInteractiveIds = useMemo(
    () => referenceLayerInteractiveIds(referenceLayers.layers),
    [referenceLayers.layers]
  );
  const referenceLayersRef = useRef(referenceLayers.layers);
  referenceLayersRef.current = referenceLayers.layers;
  // Map style based on theme: light mode = streets, dark mode = dark
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => 
    theme.palette.mode === 'light' ? 'streets' : 'dark'
  );
  const [is3DMode, setIs3DMode] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showWeatherRadar, setShowWeatherRadar] = useState(false);
  const [weatherRadarOpacity, setWeatherRadarOpacity] = useState(0.58);
  // Bust Mapbox's tile cache every 5 minutes so the IEM composite refreshes.
  const [weatherRadarEpoch, setWeatherRadarEpoch] = useState(() => Math.floor(Date.now() / 300_000));
  const [showDfpAlertHeatmap, setShowDfpAlertHeatmap] = useState(false);
  const dfpHeatmapVisible = dfpModuleEnabled && showDfpAlertHeatmap;
  const [showCameraLocations, setShowCameraLocations] = useState(false);
  const cameraOverlay = useCameraLocationsOverlay(showCameraLocations);
  const {
    data: cameraLocationsData,
    featureCount: cameraFeatureCount,
    totalCount: cameraTotalCount,
    hasActiveFilters: cameraFiltersActive,
    facets: cameraFacets,
    loading: cameraOverlayLoading,
    error: cameraOverlayError,
  } = cameraOverlay;
  // Street View (admin-gated): links on popups; thumbnails + "cameras in view" strip when a key is configured.
  const { settings: streetViewSettings } = useCameraStreetViewSettings();
  const streetViewEnabled = Boolean(streetViewSettings?.enabled);
  const streetViewPreviews = streetViewPreviewsOn(streetViewSettings);
  const [camerasInViewOpen, setCamerasInViewOpen] = useState(false);

  // COP: only cameras near live (non-stale) entities, labelled by who they cover.
  // Same prefs shape as Pathfinder's "near tracks" but stored separately.
  const [cameraNearEntities, setCameraNearEntities] = useState<CameraNearTracksPrefs>(() =>
    loadCameraNearTracksPrefs(COP_NEAR_ENTITIES_KEY, DEFAULT_COP_NEAR_ENTITIES)
  );
  const updateCameraNearEntities = useCallback((patch: Partial<CameraNearTracksPrefs>) => {
    setCameraNearEntities((prev) => {
      const next = { ...prev, ...patch };
      saveCameraNearTracksPrefs(next, COP_NEAR_ENTITIES_KEY);
      return next;
    });
  }, []);
  const liveEntityPoints = useMemo<TaggedPoint<DeviceLocationStatus>[]>(() => {
    if (!showCameraLocations) return [];
    return devices
      .filter((d) => !d.is_stale && Number.isFinite(d.latitude) && Number.isFinite(d.longitude))
      .map((d) => ({ lon: d.longitude, lat: d.latitude, tag: d }));
  }, [devices, showCameraLocations]);
  const cameraNear = useMemo(() => {
    if (!showCameraLocations || !cameraNearEntities.enabled || liveEntityPoints.length === 0) {
      return { collection: cameraLocationsData, matches: null as null };
    }
    return annotateCamerasNearPoints(cameraLocationsData, liveEntityPoints, cameraNearEntities.radiusM);
  }, [showCameraLocations, cameraNearEntities, liveEntityPoints, cameraLocationsData]);
  const cameraLayerData = cameraNear.collection;
  const cameraShownCount = cameraLayerData.features.length;
  // Feed the dashboard Legend tab (which lives outside this component).
  useEffect(() => {
    if (!onCameraLegendChange) return;
    onCameraLegendChange(
      showCameraLocations
        ? { shownCount: cameraShownCount, categories: cameraFacets.categories, loading: cameraOverlayLoading, error: cameraOverlayError }
        : null
    );
  }, [onCameraLegendChange, showCameraLocations, cameraShownCount, cameraFacets.categories, cameraOverlayLoading, cameraOverlayError]);
  useEffect(() => () => onCameraLegendChange?.(null), [onCameraLegendChange]);
  const entityLabel = useCallback((d: DeviceLocationStatus) => d.entity_id || d.device_id, []);
  const entityColor = useCallback(
    (d: DeviceLocationStatus) => COP_DEVICE_COLORS[classifyDeviceColor(d)],
    []
  );
  // Strip items: nearest-entity order, each tile captioned with the covering entity.
  const camerasNearEntityItems = useMemo<CameraInView[] | undefined>(() => {
    if (!cameraNear.matches) return undefined;
    return cameraNear.matches.map((m) => {
      const seen = new Set<string>();
      const entities: CameraNearbyEntity[] = [];
      for (const n of m.nearby) {
        const id = n.tag.device_id;
        if (seen.has(id)) continue;
        seen.add(id);
        entities.push({ id, label: entityLabel(n.tag), color: entityColor(n.tag), distanceM: n.distanceM });
      }
      return {
        camera: m.feature.properties,
        lon: m.lon,
        lat: m.lat,
        heading: cameraHeading(m.feature.properties),
        distanceM: m.nearby[0].distanceM,
        entities,
      };
    });
  }, [cameraNear.matches, entityLabel, entityColor]);
  // Lookup for the camera popup: who is near the camera the user clicked.
  // (`Map` here is react-map-gl's component, so use a plain record.)
  const nearbyByCamera = useMemo(() => {
    const byKey: Record<string, CameraNearbyEntity[]> = {};
    for (const item of camerasNearEntityItems ?? []) {
      byKey[`${item.camera.camera_id}@${item.lon.toFixed(6)},${item.lat.toFixed(6)}`] = item.entities ?? [];
    }
    return byKey;
  }, [camerasNearEntityItems]);
  const camerasNearSummary = useMemo(() => {
    if (!cameraNear.matches) return undefined;
    const n = liveEntityPoints.length;
    const shown = Math.min(cameraNear.matches.length, streetViewSettings?.in_view_limit ?? 12);
    const total = cameraNear.matches.length;
    if (total === 0) return `No cameras within ${cameraNearEntities.radiusM} m of ${n} live entit${n === 1 ? 'y' : 'ies'}`;
    const lead = total > shown ? `${shown} nearest of ${total}` : `${total}`;
    return `${lead} camera${total === 1 ? '' : 's'} within ${cameraNearEntities.radiusM} m of ${n} live entit${n === 1 ? 'y' : 'ies'}`;
  }, [cameraNear.matches, liveEntityPoints.length, cameraNearEntities.radiusM, streetViewSettings?.in_view_limit]);
  // Layer visibility / clustering / labels — one dialog, persisted per browser.
  const {
    prefs: layerPrefs,
    update: updateLayerPrefs,
    reset: resetLayerPrefs,
    isDefault: layerPrefsDefault,
  } = useCopMapLayerPrefs();
  const [layersDialogOpen, setLayersDialogOpen] = useState(false);
  // Which overlays are on — drives the Map layers button highlight/tooltip now that
  // the individual toolbar toggles have been folded into the dialog.
  const activeOverlayNames = useMemo(() => {
    const names: string[] = [];
    if (showCameraLocations) names.push('Cameras');
    if (showItinerary) names.push('Itinerary');
    if (showTraffic) names.push('Traffic + incidents');
    if (showWeatherRadar) names.push('Weather radar');
    if (dfpHeatmapVisible) names.push('DFP heatmap');
    if (referenceLayers.enabledCount > 0) {
      names.push(`${referenceLayers.enabledCount} reference layer${referenceLayers.enabledCount === 1 ? '' : 's'}`);
    }
    return names;
  }, [
    showCameraLocations,
    showItinerary,
    showTraffic,
    showWeatherRadar,
    dfpHeatmapVisible,
    referenceLayers.enabledCount,
  ]);
  const activeOverlayCount = activeOverlayNames.length;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  // Device pins, alert markers and the drone camera wedges all live in the Map
  // layers dialog (persisted per browser). The wedges describe devices, so they
  // follow the device layer.
  const showDevices = layerPrefs.devices;
  const showAlerts = layerPrefs.alerts;
  const showCameraFootprint = layerPrefs.cameraFootprint;
  const showAreaObserved = layerPrefs.areaObserved;
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [measureMode, setMeasureMode] = useState<MeasureMode>('distance');
  const [ringSpacingM, setRingSpacingM] = useState(100);
  const [ringCount, setRingCount] = useState(1);
  const mapRef = useRef<MapRef | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const geocoderRef = useRef<MapboxGeocoder | null>(null);
  const [bookmarkAnchorEl, setBookmarkAnchorEl] = useState<null | HTMLElement>(null);
  // Activity timeline docks over the bottom of the map (sibling of this map).
  // Keep the icon toolbar above it so icons are never clipped underneath.
  const [timelineBottomInset, setTimelineBottomInset] = useState(10);
  // City bookmarks for quick navigation
  const cityBookmarks = [
    { name: 'Tucson', longitude: -110.9265, latitude: 32.2217, zoom: 11 },
    { name: 'Phoenix', longitude: -112.0740, latitude: 33.4484, zoom: 11 },
    { name: 'Chicago', longitude: -87.6298, latitude: 41.8781, zoom: 11 },
    { name: 'Boston', longitude: -71.0589, latitude: 42.3601, zoom: 12 },
  ];
  
  // Store original viewport before following a device
  const originalViewportRef = useRef<{ longitude: number; latitude: number; zoom: number } | null>(null);
  
  // Track whether the next viewport change is programmatic (from parent) or user-initiated
  const isProgrammaticMoveRef = useRef(false);
  const hasAutoCenteredRef = useRef(false);

  /** Mark the next camera animation as programmatic so onMove doesn't
   *  treat it as a user pan (which would freeze Auto-zoom permanently). */
  const beginProgrammaticMove = useCallback((durationMs = 1200) => {
    isProgrammaticMoveRef.current = true;
    window.setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, durationMs);
  }, []);

  // Update viewport when mapViewState changes (from parent bookmark/auto-zoom)
  useEffect(() => {
    if (mapViewState) {
      // Parent-driven Auto-zoom / bookmark — don't treat as user pan.
      beginProgrammaticMove(1200);
      hasAutoCenteredRef.current = true;
      setViewport({
        longitude: mapViewState.longitude,
        latitude: mapViewState.latitude,
        zoom: mapViewState.zoom
      });
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [mapViewState.longitude, mapViewState.latitude],
          zoom: mapViewState.zoom,
          duration: 1000
        });
      }
    }
  }, [mapViewState, beginProgrammaticMove]);

  // Resize map when sidebar opens/closes or mobile panel size changes - gives time for CSS transition
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.getMap().resize();
      }
    }, 350); // Wait for panel transition to complete
    return () => clearTimeout(timer);
  }, [sidebarOpen, mobilePanelSize]);

  // Clear the toolbar above the activity timeline overlay (collapsed or
  // expanded). TimelineWidget sets id="cop-timeline-widget" on its Paper.
  useEffect(() => {
    let observer: ResizeObserver | null = null;
    let observedEl: Element | null = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      const el = document.getElementById('cop-timeline-widget');
      if (!el || el === observedEl) return;
      if (observer && observedEl) {
        observer.unobserve(observedEl);
      }
      observedEl = el;
      if (!observer) {
        observer = new ResizeObserver((entries) => {
          const height = entries[0]?.contentRect.height ?? 0;
          // Hidden (display:none) reports 0 — use a small default inset.
          setTimelineBottomInset(height > 0 ? Math.ceil(height) + 8 : 10);
        });
      }
      observer.observe(el);
      // Seed immediately so first paint isn't wrong before the first RO tick
      const rect = el.getBoundingClientRect();
      setTimelineBottomInset(rect.height > 0 ? Math.ceil(rect.height) + 8 : 10);
    };

    attach();
    // Timeline mounts as a sibling after the map — watch briefly until found
    const retry = window.setInterval(attach, 250);
    const stopRetry = window.setTimeout(() => window.clearInterval(retry), 5000);

    return () => {
      cancelled = true;
      window.clearInterval(retry);
      window.clearTimeout(stopRetry);
      observer?.disconnect();
    };
  }, []);

  // One-shot guards for the status / SOS auto-fit effects below. Without
  // these, every poll cycle re-fits the map and snaps the user out of any
  // manual zoom/pan they did since the last refresh.
  const hasFitStatusRef = useRef(false);
  const seenSosIdsRef = useRef<Set<string | number>>(new Set());

  // Follow selected device - zoom and track, or restore original view when unselected
  // Also continuously track when device position updates
  // Look up current position from devices array to get live updates (followedDevice is a stale snapshot)
  const liveFollowedDevice = useMemo(() => {
    if (!followedDevice) return null;
    return devices.find(d => d.device_id === followedDevice.device_id) || followedDevice;
  }, [followedDevice, devices]);

  useEffect(() => {
    if (liveFollowedDevice && mapRef.current) {
      // Store original viewport if not already stored
      if (!originalViewportRef.current) {
        const map = mapRef.current.getMap();
        const center = map.getCenter();
        originalViewportRef.current = {
          longitude: center.lng,
          latitude: center.lat,
          zoom: map.getZoom()
        };
      }

      // Mark as programmatic so a follow tick does not look like a user pan
      // and immediately clear follow.
      beginProgrammaticMove(600);
      mapRef.current.easeTo({
        center: [liveFollowedDevice.longitude, liveFollowedDevice.latitude],
        zoom: 16,
        duration: 500
      });
    } else if (!liveFollowedDevice && originalViewportRef.current && mapRef.current) {
      beginProgrammaticMove(1100);
      mapRef.current.flyTo({
        center: [originalViewportRef.current.longitude, originalViewportRef.current.latitude],
        zoom: originalViewportRef.current.zoom,
        duration: 1000
      });
      originalViewportRef.current = null;
    }
  }, [liveFollowedDevice, liveFollowedDevice?.latitude, liveFollowedDevice?.longitude, beginProgrammaticMove]);

  // One-shot fly when a device is selected without follow. Continuous tracking
  // is opt-in; selecting should not hijack the COP.
  const lastSelectedFlyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapRef.current) return;
    if (!selectedDevice) {
      lastSelectedFlyRef.current = null;
      return;
    }
    if (followedDevice) return;
    if (lastSelectedFlyRef.current === selectedDevice.device_id) return;
    lastSelectedFlyRef.current = selectedDevice.device_id;
    beginProgrammaticMove(800);
    mapRef.current.flyTo({
      center: [selectedDevice.longitude, selectedDevice.latitude],
      zoom: Math.max(14, mapRef.current.getZoom?.() ?? 14),
      duration: 700,
    });
  }, [selectedDevice, followedDevice, beginProgrammaticMove]);

  // Zoom to fit all status entries — ONLY the first time they arrive.
  // statusEntries refreshes every poll cycle; without the one-shot guard
  // this fitBounds repeatedly hijacks the user's manual pan/zoom.
  // If the parent is controlling the view via mapViewState, skip entirely.
  useEffect(() => {
    if (hasFitStatusRef.current) return;
    if (mapViewState !== null) return;
    if (!mapRef.current) return;

    const validEntries = statusEntries.filter(
      entry => entry.latitude && entry.longitude
    );
    if (validEntries.length === 0) return;

    hasFitStatusRef.current = true;
    beginProgrammaticMove(1200);

    const lats = validEntries.map(e => e.latitude);
    const lngs = validEntries.map(e => e.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const padding = 0.01; // ~1km padding
    const bounds: [[number, number], [number, number]] = [
      [minLng - padding, minLat - padding],
      [maxLng + padding, maxLat + padding]
    ];

    mapRef.current.fitBounds(bounds, {
      padding: { top: 80, bottom: 80, left: 80, right: 80 },
      duration: 1000,
      maxZoom: 16 // Don't zoom in too close if points are clustered
    });
  }, [statusEntries, mapViewState, beginProgrammaticMove]);

  // Zoom to fit SOS messages — but only for NEW ones. SOS is high-priority
  // alerting (analyst probably wants to see a fresh event), but we must not
  // re-fly to messages we've already shown or every poll snaps the map back.
  // We track shown IDs in a ref-set; new arrivals trigger fitBounds, repeats
  // are ignored.
  useEffect(() => {
    if (mapViewState !== null) return;
    if (!mapRef.current) return;
    if (sosMessages.length === 0) return;

    const validMessages = sosMessages.filter(
      msg => msg.latitude && msg.longitude
    );
    if (validMessages.length === 0) return;

    // Identify messages we haven't seen yet. Fall back to lat/lon/timestamp
    // composite when the message lacks an explicit id field.
    const newMessages = validMessages.filter(msg => {
      const id =
        (msg as any).id ??
        `${msg.latitude}:${msg.longitude}:${(msg as any).timestamp ?? ''}`;
      if (seenSosIdsRef.current.has(id)) return false;
      seenSosIdsRef.current.add(id);
      return true;
    });

    if (newMessages.length === 0) return;

    beginProgrammaticMove(1700);

    if (newMessages.length === 1) {
      const msg = newMessages[0];
      mapRef.current.flyTo({
        center: [msg.longitude, msg.latitude],
        zoom: 15,
        duration: 1500
      });
    } else {
      const lats = newMessages.map(m => m.latitude);
      const lngs = newMessages.map(m => m.longitude);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const padding = 0.01;
      const bounds: [[number, number], [number, number]] = [
        [minLng - padding, minLat - padding],
        [maxLng + padding, maxLat + padding]
      ];

      mapRef.current.fitBounds(bounds, {
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        duration: 1500,
        maxZoom: 16
      });
    }
  }, [sosMessages, mapViewState, beginProgrammaticMove]);

  // Calculate devices within current map bounds
  const calculateDevicesInBounds = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();
    const bounds = map.getBounds();

    // Emit geographic bounds if callback is provided
    if (onGeoBoundsChange) {
      onGeoBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    }

    if (onAreaAlertsViewportChange) {
      onAreaAlertsViewportChange({
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        },
        zoom: map.getZoom()
      });
    }

    // Filter devices if callback is provided
    if (onMapBoundsChange) {
      const devicesInBounds = devices.filter(device => {
        const lng = device.longitude;
        const lat = device.latitude;
        return (
          lng >= bounds.getWest() &&
          lng <= bounds.getEast() &&
          lat >= bounds.getSouth() &&
          lat <= bounds.getNorth()
        );
      });

      onMapBoundsChange(devicesInBounds);
    }
  }, [devices, onMapBoundsChange, onGeoBoundsChange, onAreaAlertsViewportChange]);

  // Update devices in bounds when map moves or devices change
  useEffect(() => {
    if (mapRef.current && (onMapBoundsChange || onGeoBoundsChange || onAreaAlertsViewportChange)) {
      // Initial calculation
      calculateDevicesInBounds();
    }
  }, [devices, calculateDevicesInBounds, onMapBoundsChange, onGeoBoundsChange, onAreaAlertsViewportChange]);

  // Center map on the densest cluster of *active* devices.
  // Buckets into ~11km grid cells, picks the densest cell, then fits to
  // that cell + neighbors. Falls back to all devices when few actives.
  // Returns true when a camera move was started.
  const centerOnDevices = useCallback((): boolean => {
    if (!mapRef.current) return false;

    const isValid = (d: DeviceLocationStatus) =>
      typeof d.latitude === 'number' &&
      typeof d.longitude === 'number' &&
      Number.isFinite(d.latitude) &&
      Number.isFinite(d.longitude) &&
      !(d.latitude === 0 && d.longitude === 0);

    const withCoords = devices.filter(isValid);
    if (withCoords.length === 0) return false;

    // Prefer recently-active devices for the densest-cluster pick so a
    // stale Tucson (or any) leftover doesn't win the auto-zoom.
    const active = withCoords.filter((d) => (d.last_seen_minutes_ago ?? 999) <= 30);
    const pool = active.length > 0 ? active : withCoords;

    let cluster = pool;
    if (pool.length > 2) {
      const CELL = 0.1; // ~11 km
      // Plain object instead of `new Map()` because `Map` is imported from
      // react-map-gl and shadows the global Map constructor in this file.
      const buckets: Record<string, typeof pool> = {};
      for (const d of pool) {
        const key = `${Math.round(d.latitude / CELL)}|${Math.round(d.longitude / CELL)}`;
        if (buckets[key]) buckets[key].push(d);
        else buckets[key] = [d];
      }

      let bestKey = '';
      let bestCount = 0;
      for (const key of Object.keys(buckets)) {
        const arr = buckets[key];
        if (arr.length > bestCount) {
          bestCount = arr.length;
          bestKey = key;
        }
      }

      const [latIdxStr, lonIdxStr] = bestKey.split('|');
      const latIdx = parseInt(latIdxStr, 10);
      const lonIdx = parseInt(lonIdxStr, 10);
      const neighborhood: typeof pool = [];
      for (let dlat = -1; dlat <= 1; dlat++) {
        for (let dlon = -1; dlon <= 1; dlon++) {
          const arr = buckets[`${latIdx + dlat}|${lonIdx + dlon}`];
          if (arr) neighborhood.push(...arr);
        }
      }
      if (neighborhood.length > 0) cluster = neighborhood;
    }

    beginProgrammaticMove(1200);

    if (cluster.length === 1) {
      const d = cluster[0];
      mapRef.current.flyTo({
        center: [d.longitude, d.latitude],
        zoom: 14,
        duration: 1000,
      });
      return true;
    }

    const lats = cluster.map(d => d.latitude);
    const lngs = cluster.map(d => d.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const MIN_SPAN = 0.01;
    const latPad = Math.max((maxLat - minLat) * 0.15, MIN_SPAN);
    const lngPad = Math.max((maxLng - minLng) * 0.15, MIN_SPAN);
    const bounds: [[number, number], [number, number]] = [
      [minLng - lngPad, minLat - latPad],
      [maxLng + lngPad, maxLat + latPad],
    ];

    mapRef.current.fitBounds(bounds, {
      padding: { top: 80, bottom: 80, left: 80, right: 80 },
      duration: 1000,
      maxZoom: 14,
    });
    return true;
  }, [devices, beginProgrammaticMove]);

  // Auto-center on the densest active cluster the first time we have
  // mappable devices AND the map is ready. Do NOT mark as done until a
  // camera move actually starts — otherwise an early empty/not-ready pass
  // leaves the map stuck on the Tucson default forever.
  useEffect(() => {
    if (hasAutoCenteredRef.current) return;
    if (mapViewState !== null) return;
    if (devices.length === 0) return;
    if (!mapRef.current) return;
    const didCenter = centerOnDevices();
    if (didCenter) {
      hasAutoCenteredRef.current = true;
    }
  }, [devices, mapViewState, centerOnDevices]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap().zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap().zoomOut();
  }, []);

  const handleResetBearing = useCallback(() => {
    mapRef.current?.getMap().resetNorth();
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }, []);

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints, calculateDistance]);

  // Handle measurement click
  const handleMeasurementClick = useCallback((lngLat: { lng: number; lat: number }) => {
    if (!isMeasuring) return;
    setMeasurementPoints((prev) => nextMeasurePoints(measureMode, prev, [lngLat.lng, lngLat.lat]));
  }, [isMeasuring, measureMode]);

  // Clear measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  const changeMeasureMode = useCallback((mode: MeasureMode) => {
    setMeasureMode(mode);
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  // Toggle measurement mode
  const toggleMeasurement = useCallback(() => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  }, [isMeasuring, clearMeasurement]);

  // Format distance for display
  const formatDistance = useCallback((km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  }, []);

  // Handle city bookmark selection
  const handleCitySelect = useCallback((city: { name: string; longitude: number; latitude: number; zoom: number }) => {
    mapRef.current?.flyTo({
      center: [city.longitude, city.latitude],
      zoom: city.zoom,
      duration: 1000
    });
    setBookmarkAnchorEl(null); // Close menu after selection
  }, []);

  // Generate GeoJSON for geofences
  const geofencesGeoJSON = useMemo(() => {
    
    if (geofences.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = geofences.map(geofence => {
      const raw = geofence.geometry as typeof geofence.geometry | string;
      let geometry: typeof geofence.geometry | null = null;
      if (typeof raw === 'string') {
        try {
          geometry = JSON.parse(raw) as typeof geofence.geometry;
        } catch {
          geometry = null;
        }
      } else {
        geometry = raw;
      }
      return {
        type: 'Feature' as const,
        id: geofence.id,
        properties: {
          id: geofence.id,
          name: geofence.name,
          description: geofence.description
        },
        geometry,
      };
    }).filter((feature) => feature.geometry?.type === 'Polygon');

    const result = {
      type: 'FeatureCollection' as const,
      features
    };
    
    return result;
  }, [geofences]);

  // Generate GeoJSON for device markers (GPU-accelerated Symbol Layer)
  const devicesGeoJSON = useMemo(() => {
    return devicesToGeoJSON(devices, selectedDevice?.device_id);
  }, [devices, selectedDevice?.device_id]);

  // Planned routes for movements under way. One source for all of them: a COP
  // rarely has more than a handful, and a single source keeps the line under
  // one z-order rather than interleaving with whatever is drawn between.
  const missionRoutesGeoJSON = useMemo(() => {
    const features = missionRoutes
      .filter((mission) => (mission.geometry?.coordinates?.length || 0) >= 2)
      .map((mission) => ({
        type: 'Feature' as const,
        properties: {
          mission_id: mission.mission_id,
          name: mission.name,
        },
        geometry: mission.geometry,
      }));
    return { type: 'FeatureCollection' as const, features };
  }, [missionRoutes]);

  const missionThreatMarkers = useMemo(
    () =>
      missionThreats.filter(
        (event) =>
          typeof event.latitude === 'number' &&
          typeof event.longitude === 'number' &&
          Number.isFinite(event.latitude) &&
          Number.isFinite(event.longitude),
      ),
    [missionThreats],
  );

  // GeoJSON for student itinerary route entries + connecting lines (single-entity COP filter)
  const itineraryGeoJSON = useMemo(() => {
    if (!showItinerary || itinerarySubmissions.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] as any[] };
    }

    const features: any[] = [];

    itinerarySubmissions.forEach((submission) => {
      const sortedEntries = [...(submission.route_entries || [])].sort((a, b) => {
        return new Date(a.arrive_datetime).getTime() - new Date(b.arrive_datetime).getTime();
      });

      sortedEntries.forEach((entry, index) => {
        if (entry.latitude == null || entry.longitude == null) return;
        const componentLabel = (entry.route_component || '').trim();
        features.push({
          type: 'Feature',
          properties: {
            id: entry.id ?? index,
            submission_id: submission.id,
            student_number: submission.student_number,
            location_name: entry.location_name_address,
            route_type: entry.route_type,
            route_component: componentLabel,
            arrive_datetime: entry.arrive_datetime,
            depart_datetime: entry.depart_datetime,
            entry_order: index,
            stop_number: index + 1,
            marker_type: 'itinerary',
          },
          geometry: {
            type: 'Point',
            coordinates: [entry.longitude, entry.latitude],
          },
        });
      });

      if (sortedEntries.length > 1) {
        features.push({
          type: 'Feature',
          properties: {
            submission_id: submission.id,
            student_number: submission.student_number,
            route_type: 'itinerary-route',
          },
          geometry: {
            type: 'LineString',
            coordinates: sortedEntries
              .filter((e) => e.latitude != null && e.longitude != null)
              .map((e) => [e.longitude, e.latitude]),
          },
        });
      }
    });

    return { type: 'FeatureCollection' as const, features };
  }, [itinerarySubmissions, showItinerary]);

  // Projected camera footprint for aircraft; indicative wedge for LIVE phones.
  const cameraCoverageGeoJSON = useMemo(() => cameraCoverageToGeoJSON(devices), [devices]);

  // Accumulated ground area observed, for one aircraft at a time. Unioned
  // server-side because a full flight is thousands of overlapping polygons.
  // Keyed off the popup as well as the selection: clicking a pin opens the
  // popup without necessarily going through the parent's selection state.
  const [droneCoverage, setDroneCoverage] = useState<DroneCoverageResponse | null>(null);
  const droneOf = (d: DeviceLocationStatus | null | undefined) =>
    d && normalizeDeviceType(d.device_type) === 'Drone' ? d.device_id : null;
  const focusedDroneId =
    droneOf(popupInfo?.kind === 'device' ? popupInfo.device : null) ??
    droneOf(selectedDevice);

  // Resolved against the live device list rather than reusing the popup's or the
  // selection's copy, both of which are snapshots taken at click time. The HUD
  // has to keep counting up as SSE updates arrive.
  const focusedDrone = useMemo(
    () => (focusedDroneId ? devices.find((d) => d.device_id === focusedDroneId) ?? null : null),
    [devices, focusedDroneId]
  );

  useEffect(() => {
    if (!focusedDroneId) {
      setDroneCoverage(null);
      return;
    }
    let cancelled = false;
    const end = new Date();
    const start = new Date(end.getTime() - AREA_OBSERVED_WINDOW_MS);
    copApiService
      .getDroneCoverage(focusedDroneId, start, end)
      .then((res) => {
        if (!cancelled) setDroneCoverage(res.coverage ? res : null);
      })
      .catch((err) => {
        console.error('[COP] Drone coverage fetch failed:', err);
        if (!cancelled) setDroneCoverage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [focusedDroneId]);

  const dfpAlertHeatmapGeoJSON = useMemo(() => {
    const severityWeight = (severity?: string | null): number => {
      switch ((severity || '').toLowerCase()) {
        case 'critical':
          return 2.5;
        case 'warning':
          return 1.5;
        case 'info':
          return 0.75;
        default:
          return 1;
      }
    };

    const features = dfpAlerts
      .filter((alert) => {
        const status = (alert.status || '').toLowerCase();
        const feedback = (alert.feedback_verdict || '').toLowerCase();
        return (
          alert.latitude != null &&
          alert.longitude != null &&
          Number.isFinite(alert.latitude) &&
          Number.isFinite(alert.longitude) &&
          status !== 'false_positive' &&
          feedback !== 'false_positive'
        );
      })
      .map((alert) => {
        const confidence = typeof alert.confidence_score === 'number'
          ? Math.max(0.25, Math.min(alert.confidence_score, 1))
          : 1;

        return {
          type: 'Feature' as const,
          properties: {
            id: alert.id,
            severity: alert.severity || 'unknown',
            weight: severityWeight(alert.severity) * confidence,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [alert.longitude as number, alert.latitude as number],
          },
        };
      });

    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, [dfpAlerts]);

  // Watchlist hits get a dedicated, always-visible threat marker (distinct from
  // the toggled DFP heatmap) so flagged devices stand out on the map.
  const watchlistMarkers = useMemo(
    () => dfpAlerts.filter((a) => {
      const status = (a.status || '').toLowerCase();
      return (
        a.anomaly_type === 'watchlist_hit' &&
        watchlistPosition(a) != null &&
        status !== 'resolved' && status !== 'false_positive'
      );
    }),
    [dfpAlerts],
  );

  const cotravelerMarkers = useMemo(
    () => liveCotravelerAlerts(dfpAlerts).filter(
      (a) => a.latitude != null && a.longitude != null
        && Number.isFinite(a.latitude) && Number.isFinite(a.longitude),
    ),
    [dfpAlerts],
  );

  const cotravelerFollowedDevices = useMemo(
    () => devicesFollowedByCotraveler(devices, dfpAlerts),
    [devices, dfpAlerts],
  );

  // Persistent, severity-colored markers for every localized DFP alert so the
  // map reflects the alert panel by default (previously alerts only appeared in
  // the off-by-default heatmap or as watchlist reticles). Watchlist hits and
  // co-traveler follows keep their dedicated reticles, so they are excluded
  // here to avoid double markers. Deduped by ~1m coordinate: a busy sensor
  // emits many alerts from the same spot, so we collapse them into one dot
  // carrying the highest severity and a count badge.
  const dfpAlertMarkers = useMemo(() => {
    const severityRank = (sev?: string | null): number => {
      switch ((sev || '').toLowerCase()) {
        case 'critical':
          return 3;
        case 'warning':
          return 2;
        case 'info':
          return 1;
        default:
          return 0;
      }
    };

    // Plain object instead of `new Map()` because `Map` is imported from
    // react-map-gl and shadows the global Map constructor in this file.
    const byLocation: Record<string, { alert: DFPMapAlert; count: number; topRank: number }> = {};
    for (const alert of dfpAlerts) {
      const status = (alert.status || '').toLowerCase();
      const feedback = (alert.feedback_verdict || '').toLowerCase();
      if (
        alert.latitude == null ||
        alert.longitude == null ||
        !Number.isFinite(alert.latitude) ||
        !Number.isFinite(alert.longitude) ||
        alert.anomaly_type === 'watchlist_hit' ||
        alert.anomaly_type === 'cotraveler' ||
        status === 'resolved' ||
        status === 'false_positive' ||
        feedback === 'false_positive'
      ) {
        continue;
      }

      const key = `${(alert.latitude as number).toFixed(5)},${(alert.longitude as number).toFixed(5)}`;
      const rank = severityRank(alert.severity);
      const existing = byLocation[key];
      if (!existing) {
        byLocation[key] = { alert, count: 1, topRank: rank };
      } else {
        existing.count += 1;
        if (rank > existing.topRank) {
          existing.alert = alert;
          existing.topRank = rank;
        }
      }
    }

    return Object.values(byLocation);
  }, [dfpAlerts]);

  // Shown next to ALERTS in the Map layers dialog.
  const alertMarkerCount =
    dfpAlertMarkers.length + watchlistMarkers.length + cotravelerMarkers.length + missionThreatMarkers.length;

  // Hiding a pin layer also drops its open popup / selection, so nothing is
  // left anchored to a marker that is no longer on the map.
  useEffect(() => {
    if (showDevices) return;
    setPopupInfo((p) => (p?.kind === 'device' ? null : p));
    onDeviceSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDevices]);
  useEffect(() => {
    if (showAlerts) return;
    setPopupInfo((p) => (p && p.kind !== 'device' && p.kind !== 'camera' && p.kind !== 'reference' ? null : p));
  }, [showAlerts]);

  // Emitter overlay geometry: accuracy ring (polygon), movement track
  // (linestring), and connector from the estimate to each observer pin.
  const dfpEmitterGeo = useMemo(() => {
    if (!dfpEmitter?.estimate) return null;
    const { latitude: lat, longitude: lon, accuracyM } = dfpEmitter.estimate;
    const features: any[] = [];

    // Accuracy ring: circle of radius accuracyM around the estimate.
    const radius = Math.max(15, Math.min(2000, accuracyM || 60));
    const ring: number[][] = [];
    const mlat = 111320;
    const mlon = 111320 * Math.cos((lat * Math.PI) / 180);
    for (let i = 0; i <= 48; i++) {
      const th = (i / 48) * 2 * Math.PI;
      ring.push([lon + (radius * Math.cos(th)) / mlon, lat + (radius * Math.sin(th)) / mlat]);
    }
    features.push({ type: 'Feature', properties: { kind: 'accuracy' }, geometry: { type: 'Polygon', coordinates: [ring] } });

    // Movement track polyline (chronological).
    const trk = (dfpEmitter.track || []).filter((p: any) => p.latitude != null);
    if (trk.length >= 2) {
      features.push({
        type: 'Feature', properties: { kind: 'track' },
        geometry: { type: 'LineString', coordinates: trk.map((p: any) => [p.longitude, p.latitude]) },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [dfpEmitter]);

  const selectedDfpAlertColor = useMemo(() => {
    switch ((selectedDfpAlert?.severity || '').toLowerCase()) {
      case 'critical':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      default:
        return '#f44336';
    }
  }, [selectedDfpAlert?.severity]);

  // Handle click on device markers, itinerary points, or measurement
  const handleDeviceMarkerClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    // Handle measurement click first
    if (isMeasuring) {
      handleMeasurementClick(e.lngLat);
      return;
    }
    
    if (e.features && e.features.length > 0) {
      // Cluster bubbles (cameras or devices): zoom to the expansion level.
      const clusterHit = e.features.find(
        (f) => f.layer?.id === CAMERA_OVERLAY_LAYER_IDS.clusters || f.layer?.id === 'device-clusters'
      );
      if (clusterHit) {
        const clusterId = clusterHit.properties?.cluster_id;
        const sourceId =
          clusterHit.layer?.id === 'device-clusters' ? 'device-markers-source' : CAMERA_OVERLAY_LAYER_IDS.source;
        const mapboxMap = mapRef.current?.getMap?.();
        try {
          const source: any = mapboxMap?.getSource(sourceId);
          if (source?.getClusterExpansionZoom && clusterId != null) {
            source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
              if (err) return;
              mapboxMap?.easeTo({ center: e.lngLat, zoom });
            });
          }
        } catch (err) {
          console.warn('[COP] Cluster expand failed:', err);
        }
        return;
      }

      const cameraFeature = e.features.find(
        (f) =>
          f.layer?.id === CAMERA_OVERLAY_LAYER_IDS.unclustered ||
          f.layer?.id === CAMERA_OVERLAY_LAYER_IDS.labels
      );
      if (cameraFeature) {
        const camera = parseCameraFeatureProperties(
          cameraFeature.properties as Record<string, unknown> | undefined
        );
        if (camera) {
          const coords = (cameraFeature.geometry as GeoJSON.Point | undefined)?.coordinates;
          setPopupInfo({
            kind: 'camera',
            longitude: Array.isArray(coords) ? Number(coords[0]) : e.lngLat.lng,
            latitude: Array.isArray(coords) ? Number(coords[1]) : e.lngLat.lat,
            camera,
          });
          return;
        }
      }

      const itineraryFeature = e.features.find(
        (f) => f.layer?.id === 'itinerary-markers' || f.layer?.id === 'itinerary-numbers' || f.layer?.id === 'itinerary-labels'
      );
      if (itineraryFeature) {
        const coords = (itineraryFeature.geometry as GeoJSON.Point | undefined)?.coordinates;
        const longitude = Array.isArray(coords) ? Number(coords[0]) : e.lngLat.lng;
        const latitude = Array.isArray(coords) ? Number(coords[1]) : e.lngLat.lat;
        setPopupInfo({
          kind: 'itinerary',
          longitude,
          latitude,
          student_number: String(itineraryFeature.properties?.student_number || ''),
          location_name: String(itineraryFeature.properties?.location_name || ''),
          route_type: String(itineraryFeature.properties?.route_type || ''),
          route_component: String(itineraryFeature.properties?.route_component || ''),
          arrive_datetime: String(itineraryFeature.properties?.arrive_datetime || ''),
          depart_datetime: String(itineraryFeature.properties?.depart_datetime || ''),
        });
        return;
      }
      // Reference layers sit under everything else, so only open their popup when
      // nothing more specific (device / camera / itinerary) was hit.
      const refFeature = e.features.find((f) => referenceLayerIdFromMapLayer(f.layer?.id) != null);
      if (refFeature && !e.features.some((f) => f.properties?.deviceData)) {
        const layerId = referenceLayerIdFromMapLayer(refFeature.layer?.id);
        if (layerId != null) {
          const geom = refFeature.geometry as GeoJSON.Geometry | undefined;
          const pt = geom?.type === 'Point' ? geom.coordinates : null;
          setPopupInfo({
            kind: 'reference',
            longitude: pt ? Number(pt[0]) : e.lngLat.lng,
            latitude: pt ? Number(pt[1]) : e.lngLat.lat,
            layerId,
            properties: (refFeature.properties as Record<string, unknown>) || {},
          });
          return;
        }
      }
      const feature = e.features.find((f) => f.properties?.deviceData) ?? e.features[0];
      const deviceData = feature.properties?.deviceData;
      if (deviceData) {
        try {
          const device = JSON.parse(deviceData) as DeviceLocationStatus;
          setPopupInfo({ kind: 'device', device });
          onDeviceSelect(device);
        } catch (err) {
          console.error('Error parsing device data from marker click:', err);
        }
      }
    }
  }, [onDeviceSelect, isMeasuring, handleMeasurementClick]);

  // Get map style from environment or use dynamic style
  const resolvedMapStyle = useMemo(() => {
    const styleKey = mapStyle.toLowerCase();
    switch (styleKey) {
      case 'satellite':
        return 'mapbox://styles/mapbox/satellite-v9';
      case 'satellite-streets':
        return 'mapbox://styles/mapbox/satellite-streets-v12';
      case 'dark':
        return 'mapbox://styles/mapbox/dark-v11';
      case 'light':
        return 'mapbox://styles/mapbox/light-v11';
      case 'outdoors':
        return 'mapbox://styles/mapbox/outdoors-v12';
      case 'streets':
      default:
        return 'mapbox://styles/mapbox/streets-v12';
    }
  }, [mapStyle]);

  // Add 3D buildings layer
  const add3DBuildingsLayer = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    try {
      // Check if the style is loaded - if not, wait for it
      if (!map.isStyleLoaded()) {
        map.once('styledata', add3DBuildingsLayer);
        return;
      }

      // Check if the layer already exists (this is the authoritative check)
      if (map.getLayer('add-3d-buildings')) return;

      // Find the first symbol layer (for label placement)
      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer: any) => layer.type === 'symbol' && layer.layout?.['text-field']
      )?.id;

      // Add the 3D buildings layer
      map.addLayer(
        {
          id: 'add-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 15,
          paint: {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'height']
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'min_height']
            ],
            'fill-extrusion-opacity': 0.6
          }
        },
        labelLayerId
      );
    } catch (error) {
      console.error('Error adding 3D buildings layer:', error);
    }
  }, []); // We check map.getLayer() directly instead of tracking state

  // The planned route source mounts when a mission arrives, after the device
  // pins are already on the map. Mapbox paints the newest layer on top, so
  // the line covers the convoy unless we put the pins back above it.
  const raiseDeviceMarkers = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map?.getLayer?.('device-markers')) return;
    try {
      map.moveLayer('device-markers');
    } catch {
      // Style swap can race the layer list.
    }
  }, []);

  useEffect(() => {
    if (missionRoutesGeoJSON.features.length === 0) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const raise = () => raiseDeviceMarkers();
    map.once('idle', raise);
    return () => {
      map.off('idle', raise);
    };
  }, [missionRoutesGeoJSON, raiseDeviceMarkers]);

  // Handle map load and style changes. Geocoder lifecycle lives in its own
  // effect below so re-binding this callback (deps churn / remount / WebGL
  // context loss) can't produce a duplicate "Search for address" control.
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();

    // Load marker images for Symbol Layer (GPU-accelerated markers)
    if (map) {
      loadMarkerImages(map);

      // Re-load marker images when style changes (style change resets the sprite)
      map.on('style.load', () => {
        loadMarkerImages(map);
        map.once('idle', raiseDeviceMarkers);
      });
      raiseDeviceMarkers();
    }

    if (is3DMode) {
      // Add a small delay to ensure style is fully loaded
      setTimeout(() => add3DBuildingsLayer(), 100);
    }

    setTimeout(calculateDevicesInBounds, 0);

    // Devices may have arrived before the map was ready; retry densest-
    // cluster auto-center now that mapRef is usable.
    if (!hasAutoCenteredRef.current && mapViewState == null && devices.length > 0) {
      setTimeout(() => {
        if (hasAutoCenteredRef.current || mapViewState != null) return;
        if (centerOnDevices()) hasAutoCenteredRef.current = true;
      }, 50);
    }
  }, [is3DMode, add3DBuildingsLayer, calculateDevicesInBounds, centerOnDevices, devices.length, mapViewState, raiseDeviceMarkers]);

  // Geocoder lifecycle: attach exactly once per (desktop) component instance
  // and remove on unmount or when switching to mobile. Previously this
  // lived inside handleMapLoad guarded by a ref, but on remount / hot-reload
  // / WebGL context loss the old DOM could outlive the ref nulling and
  // produce a stacked second search box. Owning the full lifecycle here
  // (with `map.removeControl` on cleanup) makes duplication structurally
  // impossible.
  useEffect(() => {
    if (isMobile) return; // no search bar on mobile
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const attach = () => {
      if (geocoderRef.current) return;
      const geocoder = new MapboxGeocoder({
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
        mapboxgl: require('mapbox-gl') as any,
        marker: false,
        placeholder: 'Search for address or place...',
        proximity: {
          longitude: -110.9265,
          latitude: 32.2217,
        } as any,
      });
      map.addControl(geocoder, 'top-left');
      geocoderRef.current = geocoder;
    };

    if (map.loaded()) {
      attach();
    } else {
      map.once('load', attach);
    }

    return () => {
      map.off('load', attach);
      if (geocoderRef.current) {
        try {
          map.removeControl(geocoderRef.current);
        } catch {
          // Map may already be torn down; safe to ignore.
        }
        geocoderRef.current = null;
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (!showWeatherRadar) return;
    const id = window.setInterval(() => {
      setWeatherRadarEpoch(Math.floor(Date.now() / 300_000));
    }, 60_000);
    return () => window.clearInterval(id);
  }, [showWeatherRadar]);

  const requestAreaAlerts = useCallback((force = false) => {
    const map = mapRef.current?.getMap();
    if (!map || !onLoadAreaAlerts) return;

    const bounds = map.getBounds();
    onLoadAreaAlerts({
      bounds: {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      },
      zoom: map.getZoom(),
      force,
    });
  }, [onLoadAreaAlerts]);

  const handleTrafficToggle = useCallback(() => {
    const nextVisible = !showTraffic;
    setShowTraffic(nextVisible);
    if (nextVisible) requestAreaAlerts(false);
  }, [requestAreaAlerts, showTraffic]);

  // Re-add buildings layer when 3D mode changes or map style changes
  useEffect(() => {
    if (is3DMode && mapRef.current) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    } else if (!is3DMode && mapRef.current) {
      // Remove 3D buildings layer when switching to 2D
      const map = mapRef.current.getMap();
      if (map && map.getLayer('add-3d-buildings')) {
        map.removeLayer('add-3d-buildings');
      }
    }
  }, [is3DMode, resolvedMapStyle, add3DBuildingsLayer]);

  return (
    <Box ref={mapContainerRef} id="cop-map-container" sx={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
      {/* CSS Animations for SOS markers */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.6;
            }
            50% {
              transform: scale(1.5);
              opacity: 0.4;
            }
          }
          
          @keyframes flash {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.6;
            }
          }

          @keyframes dfpAlertRing {
            0% {
              transform: translate(-50%, -50%) scale(0.25);
              opacity: 0.95;
            }
            70% {
              opacity: 0.2;
            }
            100% {
              transform: translate(-50%, -50%) scale(2.1);
              opacity: 0;
            }
          }

          @keyframes dfpAlertCore {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              box-shadow: 0 0 8px currentColor, 0 0 14px currentColor;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.12);
              box-shadow: 0 0 12px currentColor, 0 0 22px currentColor;
            }
          }
        `}
      </style>
      
      <Map
        ref={mapRef}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        {...viewport}
        projection={{name: 'mercator'}}
        onMove={(evt) => {
          setViewport(evt.viewState);
          calculateDevicesInBounds();
          
          // Notify parent if this is a user-initiated move (not programmatic)
          if (!isProgrammaticMoveRef.current && onUserInteraction) {
            onUserInteraction();
          }
        }}
        onMoveEnd={() => {
          calculateDevicesInBounds();
          if (showTraffic) requestAreaAlerts(false);
        }}
        onClick={handleDeviceMarkerClick}
        interactiveLayerIds={[
          'device-markers',
          'device-clusters',
          'itinerary-markers',
          'itinerary-numbers',
          'itinerary-labels',
          CAMERA_OVERLAY_LAYER_IDS.clusters,
          CAMERA_OVERLAY_LAYER_IDS.unclustered,
          CAMERA_OVERLAY_LAYER_IDS.labels,
          ...referenceInteractiveIds,
        ]}
        onLoad={handleMapLoad}
        mapStyle={resolvedMapStyle}
        style={{ width: '100%', height: '100%' }}
        cursor={isDrawingMode || isMeasuring ? 'crosshair' : 'auto'}
        terrain={is3DMode ? { source: 'mapbox-dem', exaggeration: 1.5 } : undefined}
        pitch={is3DMode ? 45 : 0}
        bearing={is3DMode ? 0 : 0}
        preserveDrawingBuffer={true}
      >
        <ScaleControl position="bottom-right" />

        {/* 3D Terrain Source */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {/* Admin reference layers (boundaries / districts / POIs…). The invisible
            anchor is always mounted first; reference layers insert beneath it so
            devices, cameras and overlays stay on top even when toggled on later. */}
        <Layer id={REF_LAYERS_ANCHOR_ID} type="background" paint={{ 'background-opacity': 0 }} />
        <ReferenceLayersMapLayers layers={referenceLayers.layers} beforeId={REF_LAYERS_ANCHOR_ID} />

        {/* Mapbox Traffic v1 — same tiles the Pathfinder navigator uses.
            Google raster overlays do not paint on this Mapbox GL map. */}
        {showTraffic && (
          <Source
            id="mapbox-traffic"
            type="vector"
            url="mapbox://mapbox.mapbox-traffic-v1"
          >
            <Layer
              id="traffic-flow"
              type="line"
              source-layer="traffic"
              paint={{
                'line-color': [
                  'match',
                  ['get', 'congestion'],
                  'low', '#4CAF50',
                  'moderate', '#FFEB3B',
                  'heavy', '#FF9800',
                  'severe', '#f44336',
                  '#888888',
                ],
                'line-width': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  10, 1.5,
                  14, 3,
                  18, 6,
                ],
                'line-opacity': 0.8,
              }}
              layout={{
                'line-cap': 'round',
                'line-join': 'round',
              }}
            />
          </Source>
        )}

        {showWeatherRadar && (
          <Source
            key={`nexrad-radar-${weatherRadarEpoch}`}
            id="nexrad-radar"
            type="raster"
            tiles={[
              `https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png?t=${weatherRadarEpoch}`
            ]}
            tileSize={256}
            maxzoom={10}
            attribution="NEXRAD via Iowa Environmental Mesonet"
          >
            <Layer
              id="nexrad-radar-layer"
              type="raster"
              paint={{
                'raster-opacity': weatherRadarOpacity,
                'raster-fade-duration': 250
              }}
            />
          </Source>
        )}

        {dfpHeatmapVisible && dfpAlertHeatmapGeoJSON.features.length > 0 && (
          <Source id="dfp-alert-heatmap-source" type="geojson" data={dfpAlertHeatmapGeoJSON}>
            <Layer
              id="dfp-alert-heatmap"
              type="heatmap"
              paint={{
                'heatmap-weight': ['get', 'weight'],
                'heatmap-intensity': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  0, 0.8,
                  12, 2.5,
                  16, 4,
                ],
                'heatmap-color': [
                  'interpolate',
                  ['linear'],
                  ['heatmap-density'],
                  0, 'rgba(0, 0, 0, 0)',
                  0.2, 'rgba(33, 150, 243, 0.55)',
                  0.45, 'rgba(255, 193, 7, 0.65)',
                  0.7, 'rgba(255, 87, 34, 0.75)',
                  1, 'rgba(244, 67, 54, 0.9)',
                ],
                'heatmap-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  0, 8,
                  9, 24,
                  14, 48,
                ],
                'heatmap-opacity': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  7, 0.75,
                  15, 0.45,
                ],
              }}
            />
          </Source>
        )}

        {/* Unified Control Panel - All Controls in One Place */}
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1,
            // Leave clearance above the docked activity timeline so icons
            // (incl. itinerary) are never covered; overflow scrolls inside.
            maxHeight: `calc(100% - ${10 + timelineBottomInset}px)`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(4px)',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 0.5,
              gap: 0.25,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Keep the toolbar narrow; thin thumb so it doesn't steal map space
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
                borderRadius: 2,
              },
            }}
          >
            {/* Zoom In */}
            <Tooltip title="Zoom In" placement="left">
              <IconButton
                onClick={handleZoomIn}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Zoom Out */}
            <Tooltip title="Zoom Out" placement="left">
              <IconButton
                onClick={handleZoomOut}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Reset Compass */}
            <Tooltip title="Reset North" placement="left">
              <IconButton
                onClick={handleResetBearing}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <CompassIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Divider sx={{ my: 0.25 }} />
            
            {/* Fullscreen */}
            <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} placement="left">
              <IconButton
                onClick={toggleFullscreen}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            
            {/* My Location */}
            <Tooltip title="My Location" placement="left">
              <IconButton
                onClick={() => {
                  if (mapRef.current) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        mapRef.current?.flyTo({
                          center: [position.coords.longitude, position.coords.latitude],
                          zoom: 15,
                          duration: 1500
                        });
                      },
                      (error) => console.error('Geolocation error:', error)
                    );
                  }
                }}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Center on Devices */}
            <Tooltip title="Center on Devices" placement="left">
              <IconButton
                onClick={centerOnDevices}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <CenterIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Divider sx={{ my: 0.25 }} />
            
            {/* 3D Mode Toggle */}
            <Tooltip title={is3DMode ? 'Disable 3D' : 'Enable 3D'} placement="left">
              <IconButton
                onClick={() => setIs3DMode(!is3DMode)}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: is3DMode ? 'primary.main' : 'transparent',
                  color: is3DMode ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                  '&:hover': {
                    backgroundColor: is3DMode ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                  },
                }}
              >
                {is3DMode ? <MapIcon fontSize="small" /> : <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>3D</Typography>}
              </IconButton>
            </Tooltip>
            
            {/* Map Style Selector */}
            <Tooltip title="Map Style" placement="left">
              <IconButton
                onClick={() => setShowStyleSelector(!showStyleSelector)}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: showStyleSelector ? 'primary.main' : 'transparent',
                  color: showStyleSelector ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                  '&:hover': {
                    backgroundColor: showStyleSelector ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                  },
                }}
              >
                <SatelliteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Map layers: the single home for every layer and overlay — devices,
                alerts, drone camera wedges, cameras (+ filters), dwells, geofences, itinerary, traffic, incidents,
                weather radar (+ opacity), DFP heatmap and admin reference layers. */}
            <Tooltip
              title={
                activeOverlayCount > 0
                  ? `Map layers — ${activeOverlayCount} overlay${activeOverlayCount === 1 ? '' : 's'} on (${activeOverlayNames.join(', ')})`
                  : 'Map layers (visibility, overlays, styling)'
              }
              placement="left"
            >
              <IconButton
                onClick={() => setLayersDialogOpen(true)}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor:
                    activeOverlayCount > 0 || !layerPrefsDefault ? 'rgba(25,118,210,0.25)' : 'transparent',
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
                aria-label="Map layers"
                aria-haspopup="dialog"
              >
                <MapLayersIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Divider sx={{ my: 0.25 }} />

            {/* Cameras in view (Street View filmstrip) — admin-gated; camera visibility
                and filters live in Map layers. */}
            {showCameraLocations && streetViewEnabled && (
              <Tooltip
                title={
                  camerasInViewOpen
                    ? 'Hide camera strip'
                    : camerasNearEntityItems
                      ? streetViewPreviews
                        ? 'Cameras near entities: Street View of cameras covering live entities'
                        : 'Cameras near entities: list cameras covering live entities'
                      : streetViewPreviews
                        ? 'Cameras in view: Street View of the nearest cameras on screen'
                        : 'Cameras in view: list the nearest cameras on screen'
                }
                placement="left"
              >
                <IconButton
                  onClick={() => setCamerasInViewOpen((v) => !v)}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: camerasInViewOpen ? 'rgba(0,131,143,0.25)' : 'transparent',
                    color: theme.palette.mode === 'dark' ? 'white' : '#333',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  aria-label="Cameras in view"
                  aria-pressed={camerasInViewOpen}
                >
                  <StreetViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {/* City Bookmarks */}
            <Tooltip title="City Bookmarks" placement="left">
              <IconButton
                onClick={(e) => setBookmarkAnchorEl(e.currentTarget)}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <BookmarkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Divider sx={{ my: 0.25 }} />
            
            {/* Measurement Tool */}
            <Tooltip title={isMeasuring ? 'Stop Measuring' : 'Measure Distance'} placement="left">
              <IconButton
                onClick={toggleMeasurement}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: isMeasuring ? '#ff5722' : 'transparent',
                  color: isMeasuring ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                  '&:hover': {
                    backgroundColor: isMeasuring ? '#e64a19' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                  },
                }}
              >
                <MeasureIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Clear Measurement - Only show when measuring */}
            {measurementPoints.length > 0 && (
              <Tooltip title="Clear Measurement" placement="left">
                <IconButton
                  onClick={clearMeasurement}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>

        {/* Map Style Dropdown - Shows when style selector is active */}
        {showStyleSelector && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 10,
              right: 54,
              zIndex: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(4px)',
              borderRadius: 1,
              overflow: 'hidden',
              p: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(['streets', 'satellite', 'satellite-streets', 'dark', 'light', 'outdoors'] as MapStyleType[]).map((style) => (
                <Tooltip key={style} title={style.charAt(0).toUpperCase() + style.slice(1).replace('-', ' ')} placement="left">
                  <IconButton
                    onClick={() => {
                      setMapStyle(style);
                      setShowStyleSelector(false);
                    }}
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: mapStyle === style ? 'primary.main' : 'transparent',
                      color: mapStyle === style ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                      fontSize: '0.6rem',
                      '&:hover': {
                        backgroundColor: mapStyle === style ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 'bold', lineHeight: 1.1, textAlign: 'center' }}>
                      {style === 'streets' && 'STR'}
                      {style === 'satellite' && 'SAT'}
                      {style === 'satellite-streets' && 'HYB'}
                      {style === 'dark' && 'DRK'}
                      {style === 'light' && 'LGT'}
                      {style === 'outdoors' && 'OUT'}
                    </Typography>
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        )}

        {/* Geofence Drawing Control */}
        {isDrawingMode && onGeofenceDrawn && (
          <GeofenceDrawControl
            onCreate={onGeofenceDrawn}
            defaultMode="draw_polygon"
          />
        )}

        {/* Area Analysis Drawing Control */}
        {(isDrawingAnalysisArea || analysisAreaGeometry) && onAnalysisAreaDrawn && (
          <GeofenceDrawControl
            onCreate={(geojson: any) => {
              if (geojson.geometry) {
                onAnalysisAreaDrawn(geojson.geometry as AnalysisAreaGeometry);
              }
            }}
            onDelete={onAnalysisAreaCleared ? () => onAnalysisAreaCleared() : undefined}
            defaultMode={isDrawingAnalysisArea ? 'draw_polygon' : 'simple_select'}
            displayControlsDefault={false}
            controls={{
              polygon: true,
              trash: true,
            }}
          />
        )}

        {/* Render analysis area polygon */}
        {analysisAreaGeometry && (
          <Source 
            id="analysis-area" 
            type="geojson" 
            data={{
              type: 'Feature',
              properties: {},
              geometry: analysisAreaGeometry,
            }}
          >
            <Layer
              id="analysis-area-fill"
              type="fill"
              paint={{
                'fill-color': '#4CAF50',
                'fill-opacity': 0.15,
              }}
            />
            <Layer
              id="analysis-area-line"
              type="line"
              paint={{
                'line-color': '#4CAF50',
                'line-width': 3,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        {/* Planned route for every movement under way, drawn with a casing so
            it reads against both light and satellite basemaps. */}
        {missionRoutesGeoJSON.features.length > 0 && (
          <Source id="mission-routes" type="geojson" data={missionRoutesGeoJSON}>
            <Layer
              id="mission-routes-casing"
              type="line"
              layout={{ 'line-join': 'round', 'line-cap': 'round' }}
              paint={{
                'line-color': '#0b1f33',
                'line-width': 8,
                'line-opacity': 0.65,
              }}
            />
            <Layer
              id="mission-routes-line"
              type="line"
              layout={{ 'line-join': 'round', 'line-cap': 'round' }}
              paint={{
                'line-color': '#29b6f6',
                'line-width': 4,
                'line-opacity': 0.95,
              }}
            />
            <Layer
              id="mission-routes-label"
              type="symbol"
              layout={{
                'symbol-placement': 'line',
                'text-field': ['get', 'name'],
                'text-size': 11,
                'text-offset': [0, 1.1],
                'symbol-spacing': 400,
              }}
              paint={{
                'text-color': '#e1f5fe',
                'text-halo-color': '#0b1f33',
                'text-halo-width': 1.5,
              }}
            />
          </Source>
        )}

        {/* Threats correlated to a live route. Same reticle language as DFP so
            an operator does not have to learn a second vocabulary, tinted by
            severity rather than always red. */}
        {showAlerts &&
          missionThreatMarkers.map((event) => {
            const color = missionSeverityColor(event.severity);
            return (
              <Marker
                key={`mission-threat-${event.id}`}
                longitude={event.longitude as number}
                latitude={event.latitude as number}
                anchor="center"
              >
                <div
                  role="button"
                  tabIndex={0}
                  title={`${missionEventLabel(event.event_type)}${event.label ? ` — ${event.label}` : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMissionThreatSelect?.(event);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onMissionThreatSelect?.(event);
                    }
                  }}
                  style={{
                    position: 'relative',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: onMissionThreatSelect ? 'pointer' : 'default',
                    color,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: `1.5px solid ${color}`,
                      backgroundColor: `${color}2e`,
                      animation: 'dfpAlertRing 2.4s ease-out infinite',
                      filter: `drop-shadow(0 0 4px ${color})`,
                    }}
                  />
                  <div
                    style={{
                      position: 'relative',
                      width: 14,
                      height: 14,
                      // Diamond, so a route threat is distinguishable from a
                      // round DFP alert at a glance and in monochrome.
                      transform: 'rotate(45deg)',
                      backgroundColor: color,
                      border: '1.5px solid #ffffff',
                      boxShadow: `0 0 5px ${color}`,
                    }}
                  />
                </div>
              </Marker>
            );
          })}

        {/* Render entity routes/tracks as heatmap */}
        {entityRoutes.map((route, routeIdx) => {
          console.log(`[COPMapGL] Rendering route ${routeIdx}:`, {
            entityId: route.entityId,
            trackCount: route.tracks?.length,
            firstTrack: route.tracks?.[0],
            lastTrack: route.tracks?.[route.tracks?.length - 1]
          });
          
          if (!route.tracks || route.tracks.length < 2) {
            console.log(`[COPMapGL] Skipping route - not enough tracks`);
            return null;
          }
          
          // Calculate weight based on number of points (less weight for more points)
          const pointCount = route.tracks.length;
          // Scale weight: 1.0 for <100 points, down to 0.1 for 20k+ points
          // (Values too low make heatmap invisible)
          const weight = pointCount < 100 ? 1.0 : 
                         pointCount < 500 ? 0.8 :
                         pointCount < 1000 ? 0.5 :
                         pointCount < 5000 ? 0.3 :
                         pointCount < 10000 ? 0.2 : 0.1;
          
          // Scale intensity inversely with point count
          const baseIntensity = pointCount < 500 ? 1.5 :
                                pointCount < 2000 ? 1.0 :
                                pointCount < 10000 ? 0.7 : 0.5;
          
          // Smaller radius for dense datasets
          const baseRadius = pointCount < 500 ? 20 :
                             pointCount < 2000 ? 15 :
                             pointCount < 10000 ? 10 : 8;
          
          console.log(`[COPMapGL] Heatmap settings for ${pointCount} points:`, {
            weight,
            baseIntensity,
            baseRadius,
            radiusAtZoom0: Math.max(2, baseRadius * 0.2),
            radiusAtZoom10: baseRadius,
            radiusAtZoom15: baseRadius * 1.5
          });
          
          return (
            <Source
              key={`route-heatmap-${route.entityId}-${routeIdx}`}
              id={`route-heatmap-${route.entityId}-${routeIdx}`}
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: route.tracks.map((t) => ({
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'Point',
                    coordinates: [t.longitude, t.latitude],
                  },
                })),
              }}
            >
              {/* Heatmap layer - scaled based on point count */}
              <Layer
                id={`route-heatmap-layer-${route.entityId}-${routeIdx}`}
                type="heatmap"
                paint={{
                  // Weight scaled by point count (less weight = less intense per point)
                  'heatmap-weight': weight,
                  // Intensity scales with zoom but adjusted for point count
                  'heatmap-intensity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0, baseIntensity * 0.5,
                    10, baseIntensity,
                    15, baseIntensity * 2
                  ],
                  // Color gradient - blue to red (ensure visibility at all densities)
                  'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0, 'rgba(33, 102, 172, 0)',
                    0.1, 'rgba(103, 169, 207, 0.6)',
                    0.3, 'rgba(209, 229, 240, 0.7)',
                    0.5, 'rgba(253, 219, 199, 0.75)',
                    0.7, 'rgba(239, 138, 98, 0.85)',
                    0.9, 'rgba(178, 24, 43, 0.9)',
                    1, 'rgba(128, 0, 38, 1)'
                  ],
                  // Radius scaled by point count and zoom
                  'heatmap-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0, Math.max(2, baseRadius * 0.2),
                    10, baseRadius,
                    15, baseRadius * 1.5
                  ],
                  // Opacity
                  'heatmap-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    10, 0.9,
                    18, 0.6
                  ]
                }}
              />
            </Source>
          );
        })}

        {/* Render dwell circles - transparent red circles for dwell/stop locations */}
        {dwellCircles.length > 0 && layerPrefs.dwellCircles && (
          <Source
            id="dwell-circles-source"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: dwellCircles.map((dwell) => {
                // Convert radius in meters to approximate degrees for circle rendering
                // At the equator, 1 degree ≈ 111,320 meters
                const radiusDegrees = dwell.radiusMeters / 111320;
                const points = 32;
                const coordinates: number[][] = [];
                
                for (let i = 0; i <= points; i++) {
                  const angle = (i / points) * 2 * Math.PI;
                  const dx = radiusDegrees * Math.cos(angle);
                  const dy = radiusDegrees * Math.sin(angle);
                  coordinates.push([dwell.longitude + dx, dwell.latitude + dy]);
                }
                
                return {
                  type: 'Feature',
                  properties: {
                    id: dwell.id,
                    entityId: dwell.entityId,
                    deviceId: dwell.deviceId,
                    durationSeconds: dwell.durationSeconds,
                    // Color based on duration: longer stays = more intense red
                    intensity: Math.min(1, dwell.durationSeconds / 7200), // Max intensity at 2 hours
                  },
                  geometry: {
                    type: 'Polygon',
                    coordinates: [coordinates],
                  },
                };
              }),
            }}
          >
            {/* Fill layer for dwell circles - transparent red */}
            <Layer
              id="dwell-circles-fill"
              type="fill"
              paint={{
                'fill-color': [
                  'interpolate',
                  ['linear'],
                  ['get', 'intensity'],
                  0, 'rgba(255, 100, 100, 0.2)',    // Light red for short dwells
                  0.5, 'rgba(255, 50, 50, 0.35)',   // Medium red
                  1, 'rgba(200, 0, 0, 0.5)'         // Darker red for long dwells
                ],
                'fill-opacity': 0.6
              }}
            />
            {/* Outline layer for dwell circles */}
            <Layer
              id="dwell-circles-outline"
              type="line"
              paint={{
                'line-color': [
                  'interpolate',
                  ['linear'],
                  ['get', 'intensity'],
                  0, 'rgba(255, 100, 100, 0.6)',
                  0.5, 'rgba(255, 50, 50, 0.8)',
                  1, 'rgba(200, 0, 0, 1)'
                ],
                'line-width': 2,
                'line-opacity': 0.85
              }}
            />
          </Source>
        )}

        {/* Student itinerary submissions (markers + Component labels) */}
        {showItinerary && itinerarySubmissions.length > 0 && (
          <Source id="itinerary-data" type="geojson" data={itineraryGeoJSON}>
            <Layer
              id="itinerary-markers"
              type="circle"
              filter={['==', ['get', 'marker_type'], 'itinerary']}
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8, 6,
                  12, 10,
                  16, 14,
                  20, 18,
                ],
                'circle-color': '#00ff00',
                'circle-opacity': 0.8,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 1,
              }}
            />
            {/* Stop numbers centered on green dots (matches Itinerary tool) */}
            <Layer
              id="itinerary-numbers"
              type="symbol"
              filter={['==', ['get', 'marker_type'], 'itinerary']}
              layout={{
                visibility: layerPrefs.itineraryLabels ? 'visible' : 'none',
                'text-field': ['to-string', ['get', 'stop_number']],
                'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': [
                  'interpolate', ['linear'], ['zoom'],
                  8, 8,
                  12, 10,
                  16, 12,
                  20, 14,
                ],
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{
                'text-color': '#ffffff',
                'text-halo-color': 'rgba(0,0,0,0.55)',
                'text-halo-width': 1,
              }}
            />
            <Layer
              id="itinerary-labels"
              type="symbol"
              filter={[
                'all',
                ['==', ['get', 'marker_type'], 'itinerary'],
                ['!=', ['get', 'route_component'], ''],
              ]}
              layout={{
                visibility: layerPrefs.itineraryLabels ? 'visible' : 'none',
                'text-field': ['get', 'route_component'],
                'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 10,
                  14, 12,
                  18, 13,
                ],
                'text-offset': [0, -1.4],
                'text-anchor': 'bottom',
                'text-allow-overlap': false,
                'text-ignore-placement': false,
                'text-padding': 8,
                'text-max-width': 10,
              }}
              paint={{
                'text-color': '#00cc00',
                'text-halo-color': '#000000',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5,
              }}
            />
          </Source>
        )}

        {showCameraLocations && (
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              left: 12,
              // Sit just above the activity timeline bar (collapsed or expanded).
              bottom: timelineBottomInset + 4,
              zIndex: 2,
              px: 0.75,
              py: 0.25,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              maxWidth: 300,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(20,20,20,0.85)' : 'rgba(255,255,255,0.92)',
            }}
          >
            <CameraGlyphIcon
              sx={{ fontSize: 12, flexShrink: 0, color: cameraOverlayError ? 'error.main' : '#00838f' }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.62rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
              {cameraOverlayLoading
                ? 'Cameras (loading…)'
                : cameraOverlayError
                  ? `Cameras — ${cameraOverlayError}`
                  : cameraNear.matches
                    ? `Cameras ${cameraShownCount.toLocaleString()} near ${liveEntityPoints.length} / ${cameraTotalCount.toLocaleString()}`
                    : cameraFiltersActive
                      ? `Cameras ${cameraFeatureCount.toLocaleString()} / ${cameraTotalCount.toLocaleString()}`
                      : `Cameras ${cameraFeatureCount.toLocaleString()}`}
            </Typography>
            {cameraNear.matches && cameraShownCount === 0 && (
              <Typography
                component="button"
                variant="caption"
                onClick={() => updateCameraNearEntities({ enabled: false })}
                sx={{
                  ml: 0.5,
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  color: '#00acc1',
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  lineHeight: 1.4,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
                title="No cameras near live entities — show every camera"
              >
                Show all
              </Typography>
            )}
            {!cameraOverlayLoading && !cameraOverlayError && cameraFacets.categories.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 0.25 }}>
                {cameraFacets.categories.map((cat) => (
                  <Tooltip
                    key={cat.key}
                    title={`${cat.label}: ${cat.count.toLocaleString()}${cat.visible ? '' : ' (hidden)'} — click to toggle`}
                  >
                    <Box
                      onClick={() => cameraOverlay.setCategoryVisible(cat.key)}
                      role="button"
                      aria-pressed={cat.visible}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: cat.color,
                        border: '1px solid white',
                        opacity: cat.visible ? 1 : 0.3,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {showCameraLocations && streetViewEnabled && camerasInViewOpen && (
          <CamerasInViewStrip
            cameras={cameraLayerData}
            mapRef={mapRef}
            viewportKey={`${viewport.longitude.toFixed(5)},${viewport.latitude.toFixed(5)},${viewport.zoom.toFixed(2)}`}
            // Near-entities mode: controlled list sorted by nearest live entity, regardless of viewport.
            items={camerasNearEntityItems}
            title={camerasNearEntityItems ? 'Cameras near entities' : 'Cameras in view'}
            summary={camerasNearSummary}
            limit={streetViewSettings?.in_view_limit ?? 12}
            previews={streetViewPreviews}
            // Clear the camera count badge on the left and the icon toolbar on the right.
            bottom={timelineBottomInset + 30}
            left={12}
            right={60}
            activeCameraId={popupInfo?.kind === 'camera' ? popupInfo.camera.camera_id : null}
            onClose={() => setCamerasInViewOpen(false)}
            onSelect={(item: CameraInView) => {
              mapRef.current?.flyTo({ center: [item.lon, item.lat], zoom: Math.max(viewport.zoom, 16), duration: 600 });
              setPopupInfo({ kind: 'camera', longitude: item.lon, latitude: item.lat, camera: item.camera });
            }}
          />
        )}
        <COPMapLayersDialog
          open={layersDialogOpen}
          onClose={() => setLayersDialogOpen(false)}
          prefs={layerPrefs}
          onPrefsChange={updateLayerPrefs}
          onReset={resetLayerPrefs}
          isDefault={layerPrefsDefault}
          overlays={{
            // Traffic flow and incident pins are one control: turning traffic on
            // also requests the area alerts for the current viewport.
            traffic: showTraffic,
            onTraffic: (v) => {
              if (v !== showTraffic) handleTrafficToggle();
            },
            incidents: showTraffic,
            onIncidents: (v) => {
              if (v !== showTraffic) handleTrafficToggle();
            },
            incidentsBusy: areaAlertsLoading,
            weatherRadar: showWeatherRadar,
            onWeatherRadar: setShowWeatherRadar,
            weatherRadarOpacity,
            onWeatherRadarOpacity: setWeatherRadarOpacity,
            dfpHeatmap: dfpHeatmapVisible,
            onDfpHeatmap: setShowDfpAlertHeatmap,
            dfpHeatmapCount: dfpAlertHeatmapGeoJSON.features.length,
            dfpHeatmapAvailable: dfpModuleEnabled,
            itinerary: showItinerary,
            onItinerary: (v) => onShowItineraryChange?.(v),
            itineraryAvailable: Boolean(itineraryToggleEnabled && onShowItineraryChange),
            itineraryWindow: {
              lookbackMinutes: itineraryLookbackMinutes,
              lookaheadHours: itineraryLookaheadHours,
              onChange: onItineraryWindowChange,
              pointCount: itinerarySubmissions.length,
            },
            cameras: showCameraLocations,
            onCameras: setShowCameraLocations,
          }}
          cameraDisplay={cameraOverlay.display}
          onCameraDisplayChange={cameraOverlay.setDisplay}
          cameraFilters={{
            overlay: cameraOverlay,
            shownCount: cameraShownCount,
            proximity: {
              prefs: cameraNearEntities,
              onChange: updateCameraNearEntities,
              trackPointCount: liveEntityPoints.length,
              matchedCount: cameraShownCount,
              labels: {
                title: 'Near entities',
                empty: 'No live entities — all cameras shown.',
                noun: 'live entities',
              },
            },
          }}
          referenceLayers={referenceLayers}
          isAdmin={isAdminUser}
          onZoomToBounds={(bbox) => {
            mapRef.current?.fitBounds(
              [
                [bbox[0], bbox[1]],
                [bbox[2], bbox[3]],
              ],
              { padding: 60, duration: 800, maxZoom: 15 }
            );
          }}
          counts={{
            devices: devices.length,
            dwells: dwellCircles.length,
            geofences: geofences.length,
            cameras: cameraTotalCount,
            alerts: alertMarkerCount,
          }}
          deviceCoverage
        />

        {/* Dwell circle center markers with labels (Map layers → Dwells → Labels) */}
        {dwellCircles.length > 0 && layerPrefs.dwellLabels && (
          <Source
            id="dwell-centers-source"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: dwellCircles.map((dwell) => {
                const durationStr = dwell.durationSeconds >= 3600
                  ? `${Math.floor(dwell.durationSeconds / 3600)}h ${Math.floor((dwell.durationSeconds % 3600) / 60)}m`
                  : `${Math.floor(dwell.durationSeconds / 60)}m`;
                const visitStr = dwell.visitCount && dwell.visitCount > 1 ? ` (${dwell.visitCount}x)` : '';
                return {
                  type: 'Feature',
                  properties: {
                    id: dwell.id,
                    entityId: dwell.entityId,
                    duration: durationStr + visitStr,
                    visitCount: dwell.visitCount || 1,
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [dwell.longitude, dwell.latitude],
                  },
                };
              }),
            }}
          >
            {/* Center dot */}
            <Layer
              id="dwell-centers-dot"
              type="circle"
              paint={{
                'circle-radius': 6,
                'circle-color': '#dc3545',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
              }}
            />
            {/* Duration label */}
            <Layer
              id="dwell-centers-label"
              type="symbol"
              layout={{
                'text-field': ['get', 'duration'],
                'text-size': 11,
                'text-offset': [0, 1.5],
                'text-anchor': 'top',
              }}
              paint={{
                'text-color': '#fff',
                'text-halo-color': 'rgba(0, 0, 0, 0.75)',
                'text-halo-width': 1,
              }}
            />
          </Source>
        )}

        {/* Render geofences as polygons */}
        {geofences.length > 0 && layerPrefs.geofences && (
          <Source id="geofences" type="geojson" data={geofencesGeoJSON}>
            {/* Fill layer for all geofences */}
            <Layer
              id="geofences-fill"
              type="fill"
              paint={{
                'fill-color': [
                  'case',
                  ['==', ['get', 'id'], selectedGeofenceId || -1],
                  '#00ff00', // Green for selected
                  '#ff4444'  // Red for unselected
                ],
                'fill-opacity': [
                  'case',
                  ['==', ['get', 'id'], selectedGeofenceId || -1],
                  0.2, // More opaque for selected
                  0.1  // Less opaque for unselected
                ]
              }}
            />
            {/* Outline layer for all geofences */}
            <Layer
              id="geofences-outline"
              type="line"
              paint={{
                'line-color': [
                  'case',
                  ['==', ['get', 'id'], selectedGeofenceId || -1],
                  '#00ff00', // Green for selected
                  '#ff4444'  // Red for unselected
                ],
                'line-width': [
                  'case',
                  ['==', ['get', 'id'], selectedGeofenceId || -1],
                  3, // Thicker for selected
                  2  // Normal for unselected
                ]
              }}
            />
            {/* Labels for geofences */}
            <Layer
              id="geofences-labels"
              type="symbol"
              layout={{
                visibility: layerPrefs.geofenceLabels ? 'visible' : 'none',
                'text-field': ['get', 'name'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-anchor': 'center'
              }}
              paint={{
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 2
              }}
            />
          </Source>
        )}

        <MapTrafficIncidents
          visible={showTraffic}
          incidents={trafficIncidents}
          weatherAlerts={weatherAlerts}
        />

        {/* Ground already observed by the selected aircraft. Drawn before the
            live footprint so the instantaneous view stays legible on top. */}
        {showDevices && showAreaObserved && droneCoverage?.coverage && (
          <Source
            id="drone-area-observed-source"
            type="geojson"
            data={{
              type: 'Feature',
              properties: {},
              geometry: droneCoverage.coverage,
            }}
          >
            <Layer
              id="drone-area-observed-fill"
              type="fill"
              paint={{ 'fill-color': COP_DEVICE_COLORS.drone, 'fill-opacity': 0.12 }}
            />
            <Layer
              id="drone-area-observed-outline"
              type="line"
              paint={{
                'line-color': COP_DEVICE_COLORS.drone,
                'line-width': 1,
                'line-opacity': 0.5,
              }}
            />
          </Source>
        )}

        {/* Camera coverage: projected footprint for aircraft, wedge for phones */}
        {showDevices && showCameraFootprint && cameraCoverageGeoJSON.features.length > 0 && (
          <Source id="camera-coverage-source" type="geojson" data={cameraCoverageGeoJSON}>
            <Layer
              id="camera-coverage-fill"
              type="fill"
              paint={{
                // Cyan matches the drone pin; red keeps the phone wedge as it was.
                'fill-color': ['case', ['get', 'precise'], COP_DEVICE_COLORS.drone, '#ef4444'],
                'fill-opacity': 0.22,
              }}
            />
            <Layer
              id="camera-coverage-outline"
              type="line"
              filter={['!', ['get', 'horizonClipped']]}
              paint={{
                'line-color': ['case', ['get', 'precise'], COP_DEVICE_COLORS.drone, '#f87171'],
                'line-width': 1.5,
                'line-opacity': 0.85,
              }}
            />
            {/* Separate layer because line-dasharray is not data-driven in
                Mapbox GL. A dashed edge marks a footprint whose far side ran
                past the horizon and was clamped, so that boundary is a cutoff
                rather than where the camera stops seeing. */}
            <Layer
              id="camera-coverage-outline-clipped"
              type="line"
              filter={['get', 'horizonClipped']}
              paint={{
                'line-color': ['case', ['get', 'precise'], COP_DEVICE_COLORS.drone, '#f87171'],
                'line-width': 1.5,
                'line-opacity': 0.85,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        {/* Render device markers - GPU-ACCELERATED Symbol Layer for smooth map dragging.
            Clustering is a source option Mapbox reads once, so the Source is keyed to
            remount when the user flips it in Map layers. Pins can also be hidden from
            the toolbar pin selector (showDevices). */}
        {layerPrefs.devices && (
        <Source
          key={layerPrefs.clusterDevices ? 'device-src-clustered' : 'device-src-flat'}
          id="device-markers-source"
          type="geojson"
          data={showDevices ? devicesGeoJSON : EMPTY_FEATURE_COLLECTION}
          cluster={layerPrefs.clusterDevices}
          clusterMaxZoom={15}
          clusterRadius={40}
        >
          {layerPrefs.clusterDevices && (
            <Layer
              id="device-clusters"
              type="circle"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': '#1976d2',
                'circle-radius': ['step', ['get', 'point_count'], 14, 10, 18, 50, 23],
                'circle-opacity': 0.9,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': '#ffffff',
              }}
            />
          )}
          {layerPrefs.clusterDevices && (
            <Layer
              id="device-cluster-count"
              type="symbol"
              filter={['has', 'point_count']}
              layout={{
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 11,
                'text-allow-overlap': true,
              }}
              paint={{ 'text-color': '#ffffff' }}
            />
          )}
          {/* Pin icon layer */}
          <Layer
            id="device-markers"
            type="symbol"
            filter={['!', ['has', 'point_count']]}
            layout={{
              visibility: showDevices ? 'visible' : 'none',
              'icon-image': [
                'match',
                ['get', 'markerType'],
                'phone', 'marker-phone',
                'watch', 'marker-watch',
                'vehicle', 'marker-vehicle',
                'drone', 'marker-drone',
                'wardriver', 'marker-wardriver',
                'student', 'marker-student',
                'rp', 'marker-rp',
                'srp', 'marker-srp',
                'marker-unknown' // fallback
              ],
              'icon-size': 0.75,
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              // Text label inside the pin (Map layers → Devices → Labels)
              'text-field': layerPrefs.deviceLabels ? ['get', 'label'] : '',
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 8,
              'text-anchor': 'center',
              'text-offset': [0, -2.5], // Position text in center of pin head (adjusted for smaller icon)
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            }}
            paint={{
              'icon-opacity': ['get', 'opacity'],
              'text-color': '#ffffff',
            }}
          />
        </Source>
        )}

        {/* Camera overlays above device markers so teal dots aren't buried */}
        <CameraLocationsMapLayers
          data={cameraLayerData}
          visible={showCameraLocations}
          cluster={cameraOverlay.display.cluster}
          showLabels={cameraOverlay.display.labels}
          size={cameraOverlay.display.size}
        />

        {/* Status Entry Markers with Icons */}
        {statusEntries.map((entry, idx) => {
          if (!entry.latitude || !entry.longitude) return null;
          
          // Get icon and color based on status type
          const getStatusIconAndColor = (statusType: string): { icon: React.ReactElement; color: string } => {
            const type = statusType?.toLowerCase() || '';
            
            if (type.includes('power') || type.includes('devicepower')) {
              return { 
                icon: <PowerIcon sx={{ fontSize: 20 }} />, 
                color: '#4caf50' 
              };
            }
            if (type.includes('cellular') || type.includes('cellulardata')) {
              return { 
                icon: <CellularIcon sx={{ fontSize: 20 }} />, 
                color: '#2196f3' 
              };
            }
            if (type.includes('wifi')) {
              return { 
                icon: <WifiIcon sx={{ fontSize: 20 }} />, 
                color: '#9c27b0' 
              };
            }
            if (type.includes('bluetooth')) {
              return { 
                icon: <BluetoothIcon sx={{ fontSize: 20 }} />, 
                color: '#00bcd4' 
              };
            }
            if (type.includes('airplane')) {
              return { 
                icon: <AirplaneIcon sx={{ fontSize: 20 }} />, 
                color: '#ff9800' 
              };
            }
            
            // Default
            return { 
              icon: <CircleIcon sx={{ fontSize: 16 }} />, 
              color: '#757575' 
            };
          };
          
          const { icon, color } = getStatusIconAndColor(entry.status_type);
          
          // Determine if status is ON or OFF for styling
          const isOn = entry.status_state?.toUpperCase() === 'ON' || 
                      entry.status_state?.toUpperCase() === 'ENABLED' || 
                      entry.status_state?.toUpperCase() === 'TRUE';
          
          return (
            <Marker
              key={`status-${idx}`}
              longitude={entry.longitude}
              latitude={entry.latitude}
              anchor="center"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  border: `2px solid ${color}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  opacity: isOn ? 1 : 0.6
                }}
                title={`${entry.status_type}: ${entry.status_state}`}
              >
                <div style={{ color: color, display: 'flex', alignItems: 'center' }}>
                  {icon}
                </div>
              </div>
            </Marker>
          );
        })}

        {/* SOS Alert Markers - High Priority with Pulsing Animation */}
        {sosMessages.map((message, idx) => {
          if (!message.latitude || !message.longitude) return null;

          return (
            <Marker
              key={`sos-${message.id || idx}`}
              longitude={message.longitude}
              latitude={message.latitude}
              anchor="center"
            >
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50px',
                  height: '50px',
                  cursor: 'pointer'
                }}
                title={`EMERGENCY SOS from ${message.entity_id || message.deviceid}: ${message.sms_message}`}
              >
                {/* Pulsing outer ring */}
                <div
                  style={{
                    position: 'absolute',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(244, 67, 54, 0.6)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}
                />
                
                {/* Inner SOS marker with triangle warning icon */}
                <div
                  style={{
                    position: 'relative',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'flash 1.5s ease-in-out infinite'
                  }}
                >
                  <SOSIcon
                    sx={{
                      fontSize: 20,
                      color: '#f44336',
                      filter: 'drop-shadow(0 0 8px rgba(244, 67, 54, 0.8)) drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                      backgroundColor: 'white',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>
            </Marker>
          );
        })}

        {/* Watchlist Threat Markers - unique reticle icon for flagged devices */}
        {showAlerts &&
          watchlistMarkers.map((alert) => {
          const point = watchlistPosition(alert);
          if (!point) return null;
          const lon = point.longitude;
          const lat = point.latitude;
          return (
            <Marker
              key={`dfp-watchlist-${alert.id}`}
              longitude={lon}
              latitude={lat}
              anchor="center"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setPopupInfo({ kind: 'watchlist', alert, longitude: lon, latitude: lat });
                  onDeviceSelect(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPopupInfo({ kind: 'watchlist', alert, longitude: lon, latitude: lat });
                    onDeviceSelect(null);
                  }
                }}
                style={{
                  position: 'relative',
                  width: 26,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: '1.5px solid #f44336',
                    backgroundColor: 'rgba(244,67,54,0.18)',
                    animation: 'dfpAlertRing 2.4s ease-out infinite',
                    filter: 'drop-shadow(0 0 4px #f44336)',
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#d32f2f',
                    border: '1.5px solid #ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 5px rgba(244,67,54,0.9)',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="7" />
                    <line x1="12" y1="1" x2="12" y2="5" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="1" y1="12" x2="5" y2="12" />
                    <line x1="19" y1="12" x2="23" y2="12" />
                    <circle cx="12" cy="12" r="2" fill="#ffffff" stroke="none" />
                  </svg>
                </div>
              </div>
            </Marker>
          );
        })}

        {/* Co-traveler follower reticle — same visual language as watchlist,
            a hotter red so the two stay distinguishable. */}
        {showAlerts &&
          cotravelerMarkers.map((alert) => {
          const lon = alert.longitude as number;
          const lat = alert.latitude as number;
          return (
            <Marker
              key={`dfp-cotraveler-${alert.id}`}
              longitude={lon}
              latitude={lat}
              anchor="center"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setPopupInfo({ kind: 'cotraveler', alert, longitude: lon, latitude: lat });
                  onDeviceSelect(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPopupInfo({ kind: 'cotraveler', alert, longitude: lon, latitude: lat });
                    onDeviceSelect(null);
                  }
                }}
                style={{
                  position: 'relative',
                  width: 26,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: `1.5px solid ${COTRAVELER_PULSE_COLOR}`,
                    backgroundColor: COTRAVELER_TINT_RING,
                    animation: 'dfpAlertRing 2.4s ease-out infinite',
                    filter: `drop-shadow(0 0 4px ${COTRAVELER_PULSE_COLOR})`,
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#b3001b',
                    border: '1.5px solid #ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 5px rgba(255,23,68,0.9)',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="7" />
                    <path d="M12 8v4l2.5 1.5" />
                  </svg>
                </div>
              </div>
            </Marker>
          );
        })}

        {/* Pulse on the COP device(s) the co-traveler is following.
            Pins are bottom-anchored (32×40), so the ring sits over the icon. */}
        {showAlerts &&
          cotravelerFollowedDevices.map((device) => {
          if (!Number.isFinite(device.latitude) || !Number.isFinite(device.longitude)) return null;
          return (
            <Marker
              key={`dfp-cotraveler-follow-${device.device_id}`}
              longitude={device.longitude}
              latitude={device.latitude}
              anchor="bottom"
            >
              <div
                style={{
                  position: 'relative',
                  width: 36,
                  height: 48,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: 14,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `1.5px solid ${COTRAVELER_PULSE_COLOR}`,
                    backgroundColor: COTRAVELER_TINT_RING,
                    animation: 'dfpAlertRing 2.4s ease-out infinite',
                    filter: `drop-shadow(0 0 5px ${COTRAVELER_PULSE_COLOR})`,
                  }}
                />
              </div>
            </Marker>
          );
        })}

        {/* DFP Alert Markers - persistent, severity-colored dots for every
            localized DFP alert (excludes watchlist hits, which get a reticle) */}
        {showAlerts &&
          dfpAlertMarkers.map(({ alert, count }) => {
          const color = dfpSeverityColor(alert.severity);
          const lon = alert.longitude as number;
          const lat = alert.latitude as number;
          return (
            <Marker
              key={`dfp-alert-${alert.id}`}
              longitude={lon}
              latitude={lat}
              anchor="center"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setPopupInfo({ kind: 'dfp', alert, count, longitude: lon, latitude: lat });
                  onDeviceSelect(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPopupInfo({ kind: 'dfp', alert, count, longitude: lon, latitude: lat });
                    onDeviceSelect(null);
                  }
                }}
                style={{
                  position: 'relative',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: '1.5px solid #ffffff',
                    boxShadow: `0 0 4px ${color}`,
                  }}
                />
                {count > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -5,
                      right: -6,
                      minWidth: 12,
                      height: 12,
                      padding: '0 2px',
                      borderRadius: 6,
                      backgroundColor: '#212121',
                      color: '#fff',
                      fontSize: 8,
                      lineHeight: '12px',
                      textAlign: 'center',
                      fontWeight: 700,
                      border: '1px solid #fff',
                    }}
                  >
                    {count}
                  </div>
                )}
              </div>
            </Marker>
          );
        })}

        {/* Selected DFP Alert Strobe - highlights the alert that drove map zoom */}
        {showAlerts &&
          selectedDfpAlert?.latitude != null &&
          selectedDfpAlert?.longitude != null && (
          <Marker
            key={`dfp-selected-${selectedDfpAlert.id}`}
            longitude={selectedDfpAlert.longitude}
            latitude={selectedDfpAlert.latitude}
            anchor="center"
          >
            <div
              style={{
                position: 'relative',
                width: 52,
                height: 52,
                color: selectedDfpAlertColor,
                pointerEvents: 'none',
              }}
              title={`DFP alert ${selectedDfpAlert.id}`}
            >
              {[0, 1, 2].map((ring) => (
                <div
                  key={ring}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `2px solid ${selectedDfpAlertColor}`,
                    backgroundColor: `${selectedDfpAlertColor}22`,
                    animation: 'dfpAlertRing 2.4s ease-out infinite',
                    animationDelay: `${ring * 0.55}s`,
                    animationFillMode: 'both',
                    filter: `drop-shadow(0 0 6px ${selectedDfpAlertColor})`,
                  }}
                />
              ))}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: selectedDfpAlertColor,
                  border: '1.5px solid white',
                  animation: 'dfpAlertCore 1.2s ease-in-out infinite',
                }}
              />
            </div>
          </Marker>
        )}

        {/* Emitter localization overlay: accuracy ring + movement track */}
        {dfpEmitterGeo && (
          <Source id="dfp-emitter" type="geojson" data={dfpEmitterGeo as any}>
            <Layer
              id="dfp-emitter-accuracy-fill" type="fill"
              filter={['==', ['get', 'kind'], 'accuracy']}
              paint={{ 'fill-color': '#00e5ff', 'fill-opacity': 0.12 }}
            />
            <Layer
              id="dfp-emitter-accuracy-line" type="line"
              filter={['==', ['get', 'kind'], 'accuracy']}
              paint={{ 'line-color': '#00e5ff', 'line-width': 1.5, 'line-dasharray': [2, 2] }}
            />
            <Layer
              id="dfp-emitter-track" type="line"
              filter={['==', ['get', 'kind'], 'track']}
              paint={{ 'line-color': '#ffab40', 'line-width': 2.5, 'line-opacity': 0.85 }}
            />
          </Source>
        )}
        {/* Emitter estimate pin (distinct diamond from the observer-GPS ring) */}
        {dfpEmitter?.estimate && (
          <Marker
            key={`dfp-emitter-${dfpEmitter.alertId}`}
            longitude={dfpEmitter.estimate.longitude}
            latitude={dfpEmitter.estimate.latitude}
            anchor="center"
          >
            <div
              title={`Estimated emitter: ${dfpEmitter.label || dfpEmitter.identifier}\n${dfpEmitter.estimate.method} · ${dfpEmitter.estimate.observerCount} observers${dfpEmitter.estimate.accuracyM != null ? ` · ±${Math.round(dfpEmitter.estimate.accuracyM)}m` : ''}`}
              style={{
                width: 18, height: 18, backgroundColor: '#00e5ff',
                border: '2px solid #ffffff', transform: 'rotate(45deg)',
                boxShadow: '0 0 10px #00e5ff', cursor: 'help',
              }}
            />
          </Marker>
        )}
        {/* Registered tower position (cellular), if known from OpenCelliD */}
        {dfpEmitter?.registeredTower && (
          <Marker
            key={`dfp-tower-${dfpEmitter.alertId}`}
            longitude={dfpEmitter.registeredTower.longitude}
            latitude={dfpEmitter.registeredTower.latitude}
            anchor="center"
          >
            <div
              title={`Registered tower (OpenCelliD)${dfpEmitter.registeredTower.range_m != null ? ` · range ${Math.round(dfpEmitter.registeredTower.range_m)}m` : ''}`}
              style={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#4caf50', border: '2px solid #ffffff',
                boxShadow: '0 0 8px #4caf50', cursor: 'help',
              }}
            />
          </Marker>
        )}

        {/* Compact LIVE badges on streaming pins (click opens map-tied pip) */}
        {devices
          .filter((d) => d.is_streaming && d.device_id !== watchingLiveDeviceId)
          .map((d) => (
            <Marker
              key={`live-badge-${d.device_id}`}
              longitude={d.longitude}
              latitude={d.latitude}
              anchor="bottom"
              offset={[0, -22]}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Pip only — do not open the white device info popup.
                  setPopupInfo(null);
                  setWatchingLiveDeviceId(d.device_id);
                  onDeviceSelect(d);
                }}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  padding: '2px 6px',
                  borderRadius: 3,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                }}
                title={`Watch live: ${d.device_id}`}
              >
                LIVE
              </button>
            </Marker>
          ))}

        {/* Map-tied live viewer — rides the device track as SSE updates lat/lon.
            key=deviceId keeps the LiveKit Room mounted across position ticks. */}
        {watchingLiveDeviceId && pipAnchor && (
          <Marker
            longitude={pipAnchor.longitude}
            latitude={pipAnchor.latitude}
            anchor="bottom"
            offset={[0, -28]}
          >
            <LiveStreamPlayer
              key={watchingLiveDeviceId}
              variant="pip"
              deviceId={watchingLiveDeviceId}
              label={
                livePipDevice?.entity_id ||
                livePipDevice?.device_id ||
                watchingLiveDeviceId
              }
              onClose={() => setWatchingLiveDeviceId(null)}
            />
          </Marker>
        )}

        {/* Camera / reference-layer popups — shared intel card (Street View thumbnails,
            "covers" entities, layer styling). */}
        {popupInfo && (popupInfo.kind === 'camera' || popupInfo.kind === 'reference') && (() => {
          const closePopup = () => setPopupInfo(null);
          const refView =
            popupInfo.kind === 'reference'
              ? referenceLayers.layers.find((v) => v.layer.id === popupInfo.layerId) ?? null
              : null;
          return (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              anchor="top"
              onClose={closePopup}
              closeButton={false}
              closeOnClick={false}
              className={INTEL_POPUP_CLASS}
              maxWidth="340px"
            >
              {popupInfo.kind === 'camera' ? (
                <CameraLocationPopupContent
                  camera={popupInfo.camera}
                  onClose={closePopup}
                  extraRows={(
                    nearbyByCamera[
                      `${popupInfo.camera.camera_id}@${popupInfo.longitude.toFixed(6)},${popupInfo.latitude.toFixed(6)}`
                    ] ?? []
                  ).map((e, i) => ({
                    label: i === 0 ? 'Covers' : '',
                    value: (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                        <Box
                          component="span"
                          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: e.color, border: '1px solid rgba(255,255,255,0.7)' }}
                        />
                        <span>{e.label}</span>
                        <Box component="span" sx={{ color: INTEL_COLORS.textDim, fontSize: '0.68rem' }}>
                          {e.distanceM < 1000 ? `${Math.round(e.distanceM)} m` : `${(e.distanceM / 1000).toFixed(1)} km`}
                        </Box>
                      </Box>
                    ),
                  }))}
                />
              ) : refView ? (
                <ReferenceFeaturePopupContent
                  layer={refView.layer}
                  style={refView.style}
                  properties={popupInfo.properties}
                  coords={{ lat: popupInfo.latitude, lon: popupInfo.longitude }}
                  onClose={closePopup}
                />
              ) : (
                <IntelPopupCard title="Reference feature" onClose={closePopup} rows={[]} />
              )}
            </Popup>
          );
        })()}

        {/* Shared dark command-card popup (devices, DFP alerts, itinerary stops) */}
        {popupInfo && popupInfo.kind !== 'camera' && popupInfo.kind !== 'reference' && (
          <Popup
            longitude={
              popupInfo.kind === 'device' ? popupInfo.device.longitude : popupInfo.longitude
            }
            latitude={
              popupInfo.kind === 'device' ? popupInfo.device.latitude : popupInfo.latitude
            }
            anchor="bottom"
            onClose={() => {
              setPopupInfo(null);
              if (popupInfo.kind === 'device') onDeviceSelect(null);
            }}
            closeButton={false}
            closeOnClick={false}
            className={DARK_MAP_POPUP_CLASS}
            maxWidth="320px"
            style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
          >
            {popupInfo.kind === 'itinerary' ? (
              <DarkMapPopupShell
                accent="#22c55e"
                title="Itinerary Entry"
                onClose={() => setPopupInfo(null)}
              >
                <DarkMapPopupRow label={singular}>
                  {popupInfo.student_number || 'N/A'}
                </DarkMapPopupRow>
                <OptionalPopupRow label="Location" value={popupInfo.location_name} />
                <DarkMapPopupRow label="Route">
                  {popupInfo.route_type || 'N/A'}
                </DarkMapPopupRow>
                <DarkMapPopupRow label="Component">
                  {popupInfo.route_component || 'N/A'}
                </DarkMapPopupRow>
                <OptionalPopupRow
                  label="Arrive"
                  value={
                    popupInfo.arrive_datetime
                      ? new Date(popupInfo.arrive_datetime).toLocaleString()
                      : null
                  }
                />
                <OptionalPopupRow
                  label="Depart"
                  value={
                    popupInfo.depart_datetime
                      ? new Date(popupInfo.depart_datetime).toLocaleString()
                      : null
                  }
                />
                <DarkMapPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
              </DarkMapPopupShell>
            ) : popupInfo.kind === 'device' ? (
              <DarkMapPopupShell
                accent={
                  COP_DEVICE_COLORS[classifyDeviceColor(popupInfo.device)] ||
                  COP_DEVICE_COLORS.unknown
                }
                title={popupInfo.device.device_id}
                badge={popupInfo.device.device_type || undefined}
                onClose={() => {
                  setPopupInfo(null);
                  onDeviceSelect(null);
                }}
                actions={intelEnabled ? (
                  <button
                    type="button"
                    style={darkMapPopupActionBtnStyle(true)}
                    onClick={() => setPromoteTarget({
                      member_type: 'device',
                      member_id: popupInfo.device.device_id,
                      label: popupInfo.device.entity_id || popupInfo.device.device_id,
                      is_primary: true,
                    })}
                  >
                    Add to packet
                  </button>
                ) : undefined}
              >
                {popupInfo.device.is_streaming && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Chip
                      label="LIVE"
                      size="small"
                      sx={{ height: 18, bgcolor: '#dc2626', color: '#fff', fontWeight: 800, fontSize: 10 }}
                    />
                  </Box>
                )}
                {popupInfo.device.entity_id && (
                  <DarkMapPopupRow label="Entity" mono>
                    {popupInfo.device.entity_id}
                  </DarkMapPopupRow>
                )}
                <DarkMapPopupRow label="Type">
                  {popupInfo.device.device_type || 'Unknown'}
                </DarkMapPopupRow>
                <DarkMapPopupRow label={courseSingular}>
                  {popupInfo.device.course_id || 'N/A'}
                </DarkMapPopupRow>
                <DarkMapPopupRow label="Seen">
                  {popupInfo.device.last_seen_minutes_ago !== null
                    ? `${popupInfo.device.last_seen_minutes_ago} min ago`
                    : 'Unknown'}
                </DarkMapPopupRow>
                {popupInfo.device.speed !== null && popupInfo.device.speed !== undefined && (
                  <DarkMapPopupRow label="Speed">
                    {formatSpeedMph(popupInfo.device.speed)}
                  </DarkMapPopupRow>
                )}
                <OptionalPopupRow label="Course" value={formatBearing(popupInfo.device.bearing)} />
                {popupInfo.device.battery_level !== null && (
                  <DarkMapPopupRow label="Batt.">
                    {popupInfo.device.battery_level}%
                  </DarkMapPopupRow>
                )}
                {normalizeDeviceType(popupInfo.device.device_type) === 'Drone' && (
                  <>
                    <OptionalPopupRow
                      label="Alt. AGL"
                      value={formatAltitudeAgl(popupInfo.device.altitude_agl_m)}
                    />
                    <OptionalPopupRow
                      label="Climb"
                      value={formatVerticalSpeed(popupInfo.device.vertical_speed_ms)}
                    />
                    <OptionalPopupRow
                      label="From home"
                      value={formatDistanceFromHome(popupInfo.device)}
                    />
                    <OptionalPopupRow
                      label="Gimbal"
                      value={formatGimbalPitch(popupInfo.device.gimbal_pitch_deg)}
                    />
                    <OptionalPopupRow
                      label="Camera"
                      value={formatBearing(
                        resolveCameraYawDeg({
                          gimbalYawDeg: popupInfo.device.gimbal_yaw_deg,
                          gimbalYawRelDeg: popupInfo.device.gimbal_yaw_rel_deg,
                          gimbalMode: popupInfo.device.gimbal_mode,
                          headingDeg: popupInfo.device.bearing,
                        })
                      )}
                    />
                    <OptionalPopupRow
                      label="Mode"
                      value={formatGimbalMode(popupInfo.device.gimbal_mode)}
                    />
                    <OptionalPopupRow
                      label="FOV"
                      value={formatFov(popupInfo.device.hfov_deg, popupInfo.device.vfov_deg)}
                    />
                    <OptionalPopupRow
                      label="GPS"
                      value={
                        popupInfo.device.satellite_count != null
                          ? `${popupInfo.device.satellite_count} sats` +
                            ` (${satelliteQuality(popupInfo.device.satellite_count)})`
                          : null
                      }
                    />
                    {popupInfo.device.is_flying != null && (
                      <DarkMapPopupRow label="State">
                        {popupInfo.device.is_flying ? 'Airborne' : 'On ground'}
                      </DarkMapPopupRow>
                    )}
                  </>
                )}
                {droneCoverage && droneCoverage.device_id === popupInfo.device.device_id && (
                  <DarkMapPopupRow label="Observed">
                    {formatArea(droneCoverage.area_m2) ?? '—'}
                    {droneCoverage.truncated ? ' (partial)' : ''}
                  </DarkMapPopupRow>
                )}
                <DarkMapPopupCoords
                  lat={popupInfo.device.latitude}
                  lon={popupInfo.device.longitude}
                />
                {popupInfo.device.is_streaming &&
                  watchingLiveDeviceId !== popupInfo.device.device_id && (
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
                      onClick={() => setWatchingLiveDeviceId(popupInfo.device.device_id)}
                    >
                      Watch live
                    </Button>
                  )}
                {watchingLiveDeviceId === popupInfo.device.device_id && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: '#fca5a5' }}>
                    Live viewer attached to pin
                  </Typography>
                )}
              </DarkMapPopupShell>
            ) : popupInfo.kind === 'cotraveler' ? (
              (() => {
                const ev = popupInfo.alert.evidence || {};
                const target =
                  ev.ssid ||
                  ev.external_id ||
                  ev.bssid ||
                  popupInfo.alert.device_id ||
                  `Alert ${popupInfo.alert.id}`;
                return (
                  <DarkMapPopupShell
                    accent={COTRAVELER_PULSE_COLOR}
                    title="Co-Traveler Follow"
                    badge={popupInfo.alert.severity || 'warning'}
                    onClose={() => setPopupInfo(null)}
                    actions={intelEnabled ? (
                      <button
                        type="button"
                        style={darkMapPopupActionBtnStyle(true)}
                        onClick={() => setPromoteTarget({
                          member_type: 'dfp_alert',
                          member_id: String(popupInfo.alert.id),
                          label: `Co-traveler ${target}`.trim(),
                          meta: { device_id: popupInfo.alert.device_id, anomaly_type: popupInfo.alert.anomaly_type },
                        })}
                      >
                        Promote
                      </button>
                    ) : undefined}
                  >
                    {popupInfo.alert.device_id && (
                      <DarkMapPopupRow label="Follower">{popupInfo.alert.device_id}</DarkMapPopupRow>
                    )}
                    {typeof ev.description === 'string' && ev.description && (
                      <DarkMapPopupRow label="Evidence">{ev.description}</DarkMapPopupRow>
                    )}
                    <DarkMapPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                  </DarkMapPopupShell>
                );
              })()
            ) : popupInfo.kind === 'watchlist' ? (
              (() => {
                const ev = popupInfo.alert.evidence || {};
                const label =
                  ev.watchlist_label || ev.watchlist_reason || 'Watchlist threat';
                const target =
                  ev.bssid ||
                  ev.address ||
                  ev.ssid ||
                  popupInfo.alert.device_id ||
                  `Alert ${popupInfo.alert.id}`;
                return (
                  <DarkMapPopupShell
                    accent="#f44336"
                    title="Watchlist Hit"
                    badge={popupInfo.alert.severity || 'critical'}
                    onClose={() => setPopupInfo(null)}
                    actions={intelEnabled ? (
                      <button
                        type="button"
                        style={darkMapPopupActionBtnStyle(true)}
                        onClick={() => setPromoteTarget({
                          member_type: 'dfp_alert',
                          member_id: String(popupInfo.alert.id),
                          label: `${label} ${target}`.trim(),
                          is_primary: true,
                          meta: { device_id: popupInfo.alert.device_id, anomaly_type: popupInfo.alert.anomaly_type },
                        })}
                      >
                        Add to packet
                      </button>
                    ) : undefined}
                  >
                    <DarkMapPopupRow label="Label">{label}</DarkMapPopupRow>
                    <DarkMapPopupRow label="Target" mono>
                      {target}
                    </DarkMapPopupRow>
                    {(() => {
                      const from = formatWatchWindow(ev.active_from);
                      const to = formatWatchWindow(ev.active_to);
                      const windowLabel = from && to && from !== to ? `${from} – ${to}` : (to || from);
                      return windowLabel ? (
                        <DarkMapPopupRow label="Active">{windowLabel}</DarkMapPopupRow>
                      ) : null;
                    })()}
                    {ev.location_source === 'last_known' && (
                      <DarkMapPopupRow label="Place">Last known</DarkMapPopupRow>
                    )}
                    {Array.isArray(ev.networks) && ev.networks.length > 0 && (
                      <DarkMapPopupRow label="Networks">{ev.networks.join(', ')}</DarkMapPopupRow>
                    )}
                    {popupInfo.alert.device_id && (
                      <DarkMapPopupRow label="Device" mono>
                        {popupInfo.alert.device_id}
                      </DarkMapPopupRow>
                    )}
                    <DarkMapPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                  </DarkMapPopupShell>
                );
              })()
            ) : (
              (() => {
                const ev = popupInfo.alert.evidence || {};
                const typeLabel = (popupInfo.alert.anomaly_type || 'DFP alert')
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase());
                const target =
                  ev.bssid ||
                  ev.address ||
                  ev.ssid ||
                  popupInfo.alert.device_id ||
                  `Alert ${popupInfo.alert.id}`;
                return (
                  <DarkMapPopupShell
                    accent={dfpSeverityColor(popupInfo.alert.severity)}
                    title={typeLabel}
                    badge={popupInfo.alert.severity || undefined}
                    onClose={() => setPopupInfo(null)}
                    actions={intelEnabled ? (
                      <button
                        type="button"
                        style={darkMapPopupActionBtnStyle(true)}
                        onClick={() => setPromoteTarget({
                          member_type: 'dfp_alert',
                          member_id: String(popupInfo.alert.id),
                          label: `${typeLabel} ${target}`.trim(),
                          is_primary: true,
                          meta: { device_id: popupInfo.alert.device_id, anomaly_type: popupInfo.alert.anomaly_type },
                        })}
                      >
                        Add to packet
                      </button>
                    ) : undefined}
                  >
                    <DarkMapPopupRow label="Target" mono>
                      {target}
                    </DarkMapPopupRow>
                    {popupInfo.alert.device_id && (
                      <DarkMapPopupRow label="Device" mono>
                        {popupInfo.alert.device_id}
                      </DarkMapPopupRow>
                    )}
                    {popupInfo.count > 1 && (
                      <DarkMapPopupRow label="Cluster">
                        {popupInfo.count} alerts here
                      </DarkMapPopupRow>
                    )}
                    {popupInfo.alert.confidence_score != null && (
                      <DarkMapPopupRow label="Conf.">
                        {Math.round(Number(popupInfo.alert.confidence_score) * 100)}%
                      </DarkMapPopupRow>
                    )}
                    <DarkMapPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                  </DarkMapPopupShell>
                );
              })()
            )}
          </Popup>
        )}

        {/* Selected Dwell Marker - Shows dwell location with pulsing circle */}
        {selectedDwell && (
          <Marker
            longitude={selectedDwell.longitude}
            latitude={selectedDwell.latitude}
            anchor="center"
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '60px',
                height: '60px',
              }}
            >
              {/* Pulsing outer ring */}
              <div
                style={{
                  position: 'absolute',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(244, 67, 54, 0.3)',
                  animation: 'pulse 2s ease-in-out infinite',
                  border: '2px solid rgba(244, 67, 54, 0.6)',
                }}
              />
              {/* Inner marker */}
              <div
                style={{
                  position: 'absolute',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: '#f44336',
                  border: '3px solid white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                ⏱
              </div>
              {/* Label - above marker, text only */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '65px',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)',
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 'bold' }}>
                  {selectedDwell.entity_id}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#ef9a9a', fontWeight: 500 }}>
                  {formatDwellDuration(selectedDwell.duration)}
                </Typography>
              </Box>
            </div>
          </Marker>
        )}

        {/* AI-Generated Markers from Smart Analytics */}
        {aiMarkers.map((marker) => (
          <Marker
            key={marker.id}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor="bottom"
          >
            <Tooltip 
              title={
                <Box>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {marker.label}
                  </Typography>
                  {marker.description && (
                    <Typography sx={{ fontSize: '0.7rem' }}>
                      {marker.description}
                    </Typography>
                  )}
                  {marker.entities && marker.entities.length > 0 && (
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8, mt: 0.5 }}>
                      {marker.entities.join(', ')}
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                  animation: 'markerPulse 2s ease-in-out infinite',
                  '@keyframes markerPulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' },
                  },
                }}
              >
                {/* Marker pin */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50% 50% 50% 0',
                    backgroundColor: marker.color || '#FF9800',
                    transform: 'rotate(-45deg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  }}
                >
                  <Typography
                    sx={{
                      transform: 'rotate(45deg)',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      color: 'white',
                      lineHeight: 1,
                    }}
                  >
                    {marker.type === 'colocation' ? <Groups sx={{ fontSize: 'inherit' }} /> : 
                     marker.type === 'dwell' ? <LocationOn sx={{ fontSize: 'inherit' }} /> : 
                     marker.type === 'alert' ? <NotificationsActive sx={{ fontSize: 'inherit' }} /> : 
                     marker.type === 'poi' ? <PushPin sx={{ fontSize: 'inherit' }} /> : <Star sx={{ fontSize: 'inherit' }} />}
                  </Typography>
                </Box>
                {/* Label */}
                <Box
                  sx={{
                    mt: 0.5,
                    px: 0.75,
                    py: 0.25,
                    backgroundColor: 'rgba(0,0,0,0.75)',
                    borderRadius: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography sx={{ fontSize: '0.6rem', color: 'white', fontWeight: 'bold' }}>
                    {marker.label}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Marker>
        ))}

        <MapMeasureLayers
          mode={measureMode}
          points={measurementPoints}
          ringSpacingM={ringSpacingM}
          ringCount={ringCount}
        />

        {measurementPoints.map((point, index) => (
          <Marker
            key={`measure-${index}`}
            longitude={point[0]}
            latitude={point[1]}
            anchor="center"
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: index === 0 ? '#4caf50' : '#ff5722',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            />
          </Marker>
        ))}
      </Map>

      {(isMeasuring || measurementPoints.length > 0) && (
        <MapMeasureReadout
          mode={measureMode}
          onModeChange={changeMeasureMode}
          points={measurementPoints}
          ringSpacingM={ringSpacingM}
          onRingSpacing={setRingSpacingM}
          ringCount={ringCount}
          onRingCount={setRingCount}
          right={showStyleSelector ? 190 : 55}
          zIndex={2}
        />
      )}

      {/* Aircraft HUD. Anchored to the map rather than the pin so the numbers
          stay put while panning, and only present when an aircraft is selected.
          The style and layer flyouts open into this same corner and sit below it
          in z-order, so it steps aside far enough to clear the wider of the two
          rather than covering a menu the operator just opened. */}
      <DroneTelemetryOverlay
        device={focusedDrone}
        coverage={droneCoverage}
        rightOffset={showStyleSelector ? 204 : 54}
      />

      {/* City Bookmarks Menu */}
      <Menu
        anchorEl={bookmarkAnchorEl}
        open={Boolean(bookmarkAnchorEl)}
        onClose={() => setBookmarkAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {cityBookmarks.map((city) => (
          <MenuItem
            key={city.name}
            onClick={() => handleCitySelect(city)}
            sx={{ minWidth: 150 }}
          >
            <ListItemIcon>
              <PlaceIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{city.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label}
      />
    </Box>
  );
};

export default COPMapGL;

